import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { confirmErpia } from './ConfirmDialog';
import APIService from '../services/api';
import { BandeauNotifiesBesoin, NotifiesLancement, useNotifiesBesoin } from './NotifiesBesoin';
import { BadgeElargi, BadgeJackpot, BadgeClone, BadgeNePasContacter, BadgeOpenToWork, BadgeManque, LienProfilSource } from './SourceurV2Suivi';
import { ajouterListeNoire } from '../services/listeNoire';
import { proposerProfils } from '../services/propositions';
import ProposerProfils from './ProposerProfils';
import SourceurPilotage, { RailCampagnes, ModaleListeNoire } from './SourceurPilotage';
import ProfilEtalon, { texteDossier } from './ProfilEtalon';
import SourceurDashboard from './SourceurDashboard';
import { useSkinV2, BoutonEssaiV2 } from '../services/skinV2';
import SourceurV2Accueil from './SourceurV2Accueil';

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });

// Tâche la plus RÉCENTE d'un kind — par task_id (croissant côté back), pas par
// position : l'ordre du tableau GET n'est pas garanti (récentes d'abord sur
// /besoins/:id/sourcing) et prendre « la dernière » remontait la plus ancienne.
// Une tâche (InMail) ou un fil appartient-il à CETTE recherche ? Le serveur
// pose `search_task_id` ; à défaut (envois d'avant ce champ), la date tranche —
// un InMail part toujours APRÈS sa recherche. Rien de plus vieux que la
// recherche ne peut être à elle.
const deCetteRecherche = (t, s) => {
  if (!s || !t) return false;
  if (t.search_task_id != null) return Number(t.search_task_id) === Number(s.task_id);
  return !!(s.created_at && t.created_at && new Date(t.created_at) >= new Date(s.created_at));
};
const latestOfKind = (tasks, kind) => {
  let best = null;
  for (const t of tasks) {
    if (t?.kind !== kind) continue;
    if (!best) { best = t; continue; }
    const a = Number(t.task_id), b = Number(best.task_id);
    const newer = (Number.isFinite(a) && Number.isFinite(b)) ? a > b
      : (t.updated_at || t.created_at || '') > (best.updated_at || best.created_at || '');
    if (newer) best = t;
  }
  return best;
};

// Nom complet sans doublon : le back envoie parfois name = « Lamia Meralli »
// ET first_name = « Lamia » — concaténer aveuglément donnait « Lamia Lamia
// Meralli ». Si name commence déjà par le prénom, on le garde tel quel.
// Le profil de référence est construit à partir des champs de la fiche —
// jamais inventé : ce qui manque est simplement absent du texte.
const profilDe = (c) => [
  c.titre || c.poste || null,
  c.experience ? `Expérience : ${c.experience}` : null,
  c.competences || c.skills ? `Compétences : ${c.competences || c.skills}` : null,
  c.ville || c.localisation ? `Localisation : ${c.ville || c.localisation}` : null,
].filter(Boolean).join('\n');

const joinName = (first, name) => {
  const f = String(first || '').trim(), n = String(name || '').trim();
  if (!f) return n || 'Profil';
  if (!n) return f;
  return n.toLowerCase().startsWith(f.toLowerCase()) ? n : `${f} ${n}`;
};
const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-CH', { day: '2-digit', month: 'short' }) + ' ' + d.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' });
};

// Chip d'état (handoff revoir 27) : ok / warn / bad / mut, dot pulsant optionnel.
const Chip = ({ kind = 'mut', pulse, mono, style, title, children }) => (
  <span className={`src-chip src-chip--${kind}`} title={title} style={{ ...(mono ? { fontFamily: "'IBM Plex Mono',monospace" } : null), ...style }}>
    {pulse && <span className="src-dot" />}{children}
  </span>
);
const scoreKind = (v) => (v >= 75 ? 'ok' : v >= 50 ? 'warn' : 'bad');
const AiBadge = ({ label = "générée par l'IA" }) => (
  <Chip kind="mut" mono style={{ fontSize: '10.5px' }}><BI icon="info-circle" /> {label}</Chip>
);

// Candidat déjà au dossier (contactKnown) : tag + coordonnées connues.
// CV capté par Sourceur sur le profil Recruiter : il sera rattaché tout seul
// à la fiche candidat le jour où elle est créée. Le champ `cvUrl` de la
// réponse n'est PAS un lien affichable (chemin technique côté Sourceur,
// protégé par leur token serveur) — le CV se consulte dans les pièces de la
// fiche, comme tout document.
const CvRecu = ({ c }) => !c?.cv ? null : (
  <Chip kind="ok" style={{ fontSize: '10.5px' }} >
    <BI icon="file-earmark-check" /> CV reçu
  </Chip>
);

const ContactKnown = ({ c }) => !c?.contactKnown ? null : (
  <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
    <Chip kind="ok" style={{ fontSize: '10.5px' }}><BI icon="person-vcard" /> Candidat connu — numéro au dossier</Chip>
    {(c.phone || c.email) && (
      <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11.5px', color: 'var(--text-2)', wordBreak: 'break-all' }}>
        {[c.phone, c.email].filter(Boolean).join(' · ')}
      </span>
    )}
  </div>
);

// ── Hook : état sourcing LinkedIn d'un besoin ────────────────────────────────
// enabled=false (flag coupé) → aucun appel réseau (pas de 403 en rafale).
export function useSourcing(besoin, { showNotification, enabled = true } = {}) {
  const besoinId = besoin?.id;
  const [tasks, setTasks] = useState([]);       // data: [{kind:'search'|'inmail', …}]
  const [loading, setLoading] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [sending, setSending] = useState(false);
  const [generating, setGenerating] = useState(false);
  // task_id du 202 du POST, repli si le GET n'a pas encore rafraîchi la tâche.
  const [lastTaskId, setLastTaskId] = useState(null);
  // Query effectivement utilisée (202) : utile quand elle a été générée par l'IA.
  const [launchInfo, setLaunchInfo] = useState(null); // { query, query_generee, explication }
  // Noms envoyés au 202 InMail : file d'attente optimiste tant que le rapport
  // (inmails[]) n'est pas revenu — sinon écran vide juste après l'envoi.
  const [sentNames, setSentNames] = useState([]);
  const sentAtRef = useRef(null);
  // Photo de la tâche search au moment du (re)lancement : l'état optimiste
  // « Recherche en cours » ne tient que tant que le GET renvoie EXACTEMENT
  // cette tâche-là (id+statut). Ne PAS comparer au task_id du 202 : il peut
  // vivre dans une autre numérotation que celle du GET, et l'optimiste
  // masquerait alors le vrai résultat jusqu'à quitter/revenir.
  const [launchMark, setLaunchMark] = useState(null); // { prevKey, at }
  const rawSearchRef = useRef(null);
  const pollBusy = useRef(false);

  const load = useCallback(async () => {
    if (besoinId == null || !enabled) return;
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/besoins/${besoinId}/sourcing`, { headers: authH() });
      if (r.ok) { const j = await r.json().catch(() => ({})); const arr = j.data || j.tasks || (Array.isArray(j) ? j : []); setTasks(Array.isArray(arr) ? arr : []); }
    } catch { /* offline / non déployé → aucune tâche */ }
    finally { setLoading(false); }
  }, [besoinId, enabled]);

  useEffect(() => { load(); }, [load]);

  const rawSearch = latestOfKind(tasks, 'search');
  rawSearchRef.current = rawSearch;
  // Après un 202, le GET peut encore renvoyer l'ANCIENNE tâche (la nouvelle
  // n'est pas encore insérée) : on affiche une recherche en cours optimiste
  // tant que le GET renvoie exactement la tâche photographiée au lancement
  // (id + statut). Dès que QUOI QUE CE SOIT change côté GET (nouvelle tâche,
  // ou même tâche re-statuée), le réel reprend la main. Garde-fou 10 min.
  const awaitingNewTask = (() => {
    if (!launchMark) return false;
    if (Date.now() - launchMark.at > 10 * 60 * 1000) return false;
    const curKey = rawSearch ? `${rawSearch.task_id}|${rawSearch.status}` : null;
    return curKey === launchMark.prevKey;
  })();
  const search = awaitingNewTask
    ? { kind: 'search', task_id: lastTaskId, status: 'pending', query: launchInfo?.query, query_generee: launchInfo?.query_generee, mode: launchInfo?.mode,
        engine: launchInfo?.engine || undefined,
        // Rapport v2 encore vide : la carte s'ouvre sur la zone visée et le
        // dira, plutôt que d'attendre la première passe pour exister.
        v2: launchInfo?.engine === 'v2'
          ? { statut: 'en_cours', passes: [], seuil: null, budget: { recherches_max: launchInfo?.max_passes ?? null, utilisees: 0 } }
          : undefined }
    : rawSearch;
  // L'envoi d'InMails de CETTE recherche — pas le dernier du besoin : une
  // campagne relancée montrait les envois et les réponses de la précédente
  // (« InMails lancés » daté d'avant « Recherche lancée ») comme les siens.
  // Recherche optimiste (pas encore en base) : rien ne peut lui appartenir.
  const inmail = awaitingNewTask ? null : latestOfKind(tasks.filter(t => t?.kind === 'inmail' && deCetteRecherche(t, rawSearch)), 'inmail');
  // Rapport d'envoi : entries = inmails[] si présent (peut être vide juste
  // après l'envoi — la tâche existe avant son rapport).
  const entries = Array.isArray(inmail?.inmails) ? inmail.inmails : null;
  // La file optimiste s'efface quand un rapport postérieur à l'envoi arrive.
  useEffect(() => {
    if (!sentNames.length || !entries || entries.length === 0) return;
    const ts = inmail?.updated_at || inmail?.created_at;
    if (!sentAtRef.current || !ts || new Date(ts).getTime() >= sentAtRef.current - 60000) setSentNames([]);
  }, [entries, inmail, sentNames.length]);

  // Poll tant que la campagne bouge : recherche en cours, envoi auto attendu
  // (sélection reçue en auto mais rapport InMails pas encore arrivé), envois
  // pas tous à l'état final, rapport d'envoi pas encore revenu.
  const searchPending = search?.status === 'pending';
  const autoAwaitingReport = search?.mode === 'auto' && search?.status === 'selection_received' && !inmail;
  const entriesPending = !!entries && entries.some(m => m.status !== 'sent' && m.status !== 'error');
  const reportAwaited = !!inmail && (!entries || entries.length === 0) && (inmail.sent ?? 0) < (inmail.total ?? 0);
  const sendAwaited = sentNames.length > 0;
  const pollActive = searchPending || autoAwaitingReport || entriesPending || reportAwaited || sendAwaited;
  useEffect(() => {
    if (besoinId == null || !enabled || !pollActive) return undefined;
    const iv = setInterval(async () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (pollBusy.current) return;
      pollBusy.current = true;
      try {
        const r = await fetch(`${BASE}/besoins/${besoinId}/sourcing`, { headers: authH() });
        if (r.ok) { const j = await r.json().catch(() => ({})); const arr = j.data || j.tasks || (Array.isArray(j) ? j : []); if (Array.isArray(arr)) setTasks(arr); }
      } catch { /* retry au tick suivant */ }
      finally { pollBusy.current = false; }
    }, 15000);
    return () => clearInterval(iv);
  }, [besoinId, enabled, pollActive]);

  // ── Le direct de la carte de ciblage ─────────────────────────────────────
  // Les passes et les profils n'existent dans l'état (`GET .../sourcing`)
  // qu'au webhook de FIN : pendant la chasse — plusieurs minutes — la carte
  // n'aurait rien à montrer. Le journal, lui, lit le suivi Sourceur en
  // direct. On ne le sonde que pour une campagne v2 en cours ; route absente
  // (404/501) → on cesse d'appeler et la carte se contente de l'état.
  const [direct, setDirect] = useState(null);
  const [directAbsent, setDirectAbsent] = useState(false);
  const directBusy = useRef(false);
  const moteurV2Actif = (search?.engine || launchInfo?.engine) === 'v2';
  const directActif = enabled && besoinId != null && moteurV2Actif && searchPending && !directAbsent;
  useEffect(() => {
    if (!directActif) return undefined;
    let annule = false;
    const lire = async () => {
      if (directBusy.current) return;
      directBusy.current = true;
      try {
        const r = await fetch(`${BASE}/besoins/${besoinId}/sourcing/journal`, { headers: authH() });
        if (r.status === 404 || r.status === 405 || r.status === 501) { if (!annule) setDirectAbsent(true); return; }
        if (!r.ok) return;
        const j = await r.json().catch(() => ({}));
        if (!annule && j.success !== false) setDirect(j.data ?? j);
      } catch { /* réseau : on retente au tick suivant */ }
      finally { directBusy.current = false; }
    };
    lire();
    const iv = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      lire();
    }, 4000);
    return () => { annule = true; clearInterval(iv); };
  }, [directActif, besoinId]);

  // Génération IA de la query booléenne (pré-remplissage du champ).
  const generateQuery = useCallback(async ({ ville } = {}) => {
    if (besoinId == null) return null;
    setGenerating(true);
    try {
      const r = await fetch(`${BASE}/besoins/${besoinId}/sourcing/query`, {
        method: 'POST', headers: jsonH(),
        body: JSON.stringify(ville?.trim() ? { ville: ville.trim() } : {}),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false || !j.query) throw new Error(j.error || `HTTP ${r.status}`);
      return j; // { query, ville, explication }
    } catch (e) { showNotification?.('Erreur génération de la query : ' + e.message, 'error'); return null; }
    finally { setGenerating(false); }
  }, [besoinId, showNotification]);

  // Query manuelle prioritaire ; sans query, le back en génère une (202 → query_generee).
  // mode: 'validate' = sélection à valider (envoi InMail manuel) ; 'auto' = InMails
  // envoyés automatiquement aux meilleurs profils sans validation.
  // experience (ex. "5+") optionnelle — absente + query IA : le back la déduit.
  // excludeCompanies : si FOURNI, la liste complète est respectée telle quelle
  // par le back ; si ABSENT, le back exclut automatiquement le client du besoin.
  const launch = useCallback(async ({ query, profile, candidatRefId, ville, topN, minScore, mode, experience, excludeCompanies, shareJd, notifies, moteurV2, sources, jackpot, jackpotEnPremier, etalonId, openToWork, maxProfils, langues, entreprises, pays }) => {
    if (besoinId == null) return false;
    setLaunching(true);
    try {
      const body = { ville: ville?.trim() || undefined, mode: mode === 'auto' ? 'auto' : 'validate', topN: Number(topN) || 5 };
      // Seuil de score : envoyé seulement s'il est renseigné — vide, le
      // serveur applique son seuil par défaut.
      if (minScore !== '' && minScore != null && Number.isFinite(Number(minScore))) {
        body.minScore = Math.max(0, Math.min(100, Number(minScore)));
      }
      if (query?.trim()) body.query = query.trim();
      // Recherche par profil type : Sourceur dérive lui-même la requête
      // booléenne du profil. On n'envoie jamais les deux à la fois.
      if (!query?.trim() && profile?.trim()) {
        body.profile = profile.trim();
        if (candidatRefId != null) body.candidat_reference_id = candidatRefId;
      }
      if (experience?.trim()) body.experience = experience.trim();
      if (Array.isArray(excludeCompanies)) body.excludeCompanies = excludeCompanies;
      if (shareJd) body.share_jd = true; // la JD publique est partagée au candidat qui répond
      // Recherche version 2 (par passes) : demandée explicitement. Champ
      // proposé au serveur — s'il l'ignore, la campagne part en standard et
      // l'écran montrera ce que le serveur a réellement fait.
      // Le moteur par passes est le seul moteur (la v1 est débranchée le
      // 14.09) : le serveur le force aussi, on le dit ici pour lire clair.
      body.engine = 'v2';
      // Canaux à interroger en plus de LinkedIn. Envoyé seulement si le
      // recruteur en a coché : sans le champ, le moteur garde LinkedIn seul.
      if (Array.isArray(sources) && sources.length) body.sources = sources;
      if (openToWork) body.open_to_work = true;
      // Profils BRUTS lus avant le tri (défaut serveur ≈ 50) : 100, 200, 400
      // pour une campagne plus large — la lecture prend d'autant plus de temps.
      if (Number(maxProfils) > 0) body.max_profils = Number(maxProfils);
      // Les mots rares guettés (jackpot) : le moteur marque les profils qui
      // les portent. Envoyé seulement s'il y en a.
      if (Array.isArray(jackpot) && jackpot.length) body.jackpot = jackpot;
      // Mode jackpot : sans — les mots sont seulement guettés, la requête
      // part telle quelle (par défaut, la première passe les cherche).
      if (jackpotEnPremier === false) body.jackpot_en_premier = false;
      // V2.1 — les entreprises où piocher (cherchées en première passe, chaque
      // profil qui en vient est marqué) et les pays où chercher les profils
      // (la ville reste celle du poste). Envoyés seulement s'il y en a ; le
      // serveur retire le client des entreprises et le dit.
      if (Array.isArray(entreprises) && entreprises.length) body.entreprises = entreprises;
      if (Array.isArray(pays) && pays.length) body.pays = pays;
      // Les langues requises par le client : chaque retenu qui ne les déclare
      // pas sur son profil sera signalé (alerte, jamais un refus). Vides, le
      // serveur prend celles du besoin.
      if (Array.isArray(langues) && langues.length) body.langues = langues;
      // Campagne de CLONES d'un profil étalon : le serveur reprend du dossier
      // ce que le recruteur n'a pas retouché (booléenne, référence de
      // scoring, jackpot, ville).
      if (etalonId) body.etalon_id = etalonId;
      // Personnes à prévenir pour CETTE campagne. Envoyé même vide : « ne
      // prévenir personne » n'est pas « laisser le serveur décider ».
      if (Array.isArray(notifies)) {
        const moiId = APIService.getCurrentUser()?.id ?? null;
        body.notifier_user_ids = (moiId != null && !notifies.some(v => String(v) === String(moiId)))
          ? [...notifies, moiId] : notifies;
      }
      const r = await fetch(`${BASE}/besoins/${besoinId}/sourcing`, {
        method: 'POST', headers: jsonH(), body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!(r.status === 202 || r.ok) || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setLastTaskId(j.task_id ?? j.taskId ?? null);
      const prev = rawSearchRef.current;
      setLaunchMark({ prevKey: prev ? `${prev.task_id}|${prev.status}` : null, at: Date.now() });
      // derivedQuery / raisonnement : la recherche que Sourceur a construite
      // à partir du profil — affichée telle qu'elle revient.
      const derivee = j.derivedQuery ?? j.derived_query ?? null;
      setLaunchInfo({
        query: derivee || j.query || body.query || '',
        query_generee: !!(j.query_generee || derivee),
        explication: j.raisonnement ?? j.explication,
        depuisProfil: j.profil === true || !!derivee || !!body.profile,
        mode: body.mode,
        // Le moteur RÉELLEMENT retenu par le serveur (il refuse la v2 s'il ne
        // l'a pas) : c'est lui qui décide si la carte de ciblage s'ouvre, dès
        // le lancement, sans attendre que le GET connaisse la tâche.
        engine: j.engine || null,
        max_passes: j.max_passes ?? null,
      });
      // Message backend affiché tel quel (recherche longue, sélection en notification).
      showNotification?.(j.message || 'Recherche LinkedIn lancée — la sélection arrivera en notification (plusieurs minutes).', 'success');
      // La société visée retirée de la requête par le serveur : on le dit,
      // sinon le recruteur cherche pourquoi sa requête a changé.
      if (Array.isArray(j.retires_de_la_requete) && j.retires_de_la_requete.length) showNotification?.(j.note || `« ${j.retires_de_la_requete.join(' », « ')} » retiré de la requête : c'est la société visée.`, 'info');
      load();
      return true;
    } catch (e) { showNotification?.('Erreur sourcing : ' + e.message, 'error'); return false; }
    finally { setLaunching(false); }
  }, [besoinId, showNotification, load]);

  const sendInmails = useCallback(async (taskId, candidates) => {
    if (!taskId || !candidates.length) return;
    // « Ne pas contacter » : le serveur refuse de toute façon (barrière
    // serveur) — ici on n'envoie pas les profils marqués, et on le dit, pour
    // ne pas découvrir le refus après le clic.
    const marques = candidates.filter(c => c.ne_pas_contacter === true);
    const aEnvoyer = candidates.filter(c => c.ne_pas_contacter !== true);
    const nomsMarques = marques.map(c => joinName(c.firstName, c.name)).join(', ');
    if (!aEnvoyer.length) {
      showNotification?.(`Aucun InMail : ${nomsMarques} — marqué${marques.length > 1 ? 's' : ''} « ne pas contacter » sur ${marques.length > 1 ? 'leur' : 'sa'} fiche.`, 'error');
      return;
    }
    setSending(true);
    try {
      const r = await fetch(`${BASE}/sourcing/${taskId}/inmail`, {
        method: 'POST', headers: jsonH(),
        body: JSON.stringify({ candidates: aEnvoyer.map(c => ({ recruiterId: c.recruiterId, name: c.name, firstName: c.firstName })) }),
      });
      const j = await r.json().catch(() => ({}));
      if (!(r.status === 202 || r.ok) || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      // File optimiste : les profils envoyés restent visibles « En file »
      // jusqu'au rapport (le GET peut renvoyer la tâche inmail sans inmails[]).
      setSentNames(aEnvoyer.map(c => joinName(c.firstName, c.name)));
      sentAtRef.current = Date.now();
      showNotification?.((j.message || `Envoi de ${aEnvoyer.length} InMail(s) lancé — statut en notification.`)
        + (marques.length ? ` ${marques.length} profil${marques.length > 1 ? 's' : ''} non envoyé${marques.length > 1 ? 's' : ''} (ne pas contacter) : ${nomsMarques}.` : ''), marques.length ? 'info' : 'success');
      load();
    } catch (e) { showNotification?.('Erreur InMail : ' + e.message, 'error'); }
    finally { setSending(false); }
  }, [showNotification, load]);

  // Garder au vivier (retenu ou sous le seuil) : POST /sourcing/:taskId/vivier
  // → { success, message, candidate_id, already } — message en toast tel quel,
  // already:true = re-clic inoffensif. keptIds pilote l'état des boutons.
  const [vivierBusyId, setVivierBusyId] = useState(null);
  const [keptIds, setKeptIds] = useState(() => new Set());
  const keepInVivier = useCallback(async (taskId, cand) => {
    if (!taskId || cand?.recruiterId == null) return;
    setVivierBusyId(cand.recruiterId);
    try {
      const r = await fetch(`${BASE}/sourcing/${taskId}/vivier`, {
        method: 'POST', headers: jsonH(), body: JSON.stringify({ recruiterId: cand.recruiterId }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(j.message || 'Profil gardé au vivier.', 'success');
      setKeptIds(prev => new Set(prev).add(cand.recruiterId));
    } catch (e) { showNotification?.('Erreur vivier : ' + e.message, 'error'); }
    finally { setVivierBusyId(null); }
  }, [showNotification]);

  // Ajouter au BESOIN : crée la fiche candidat et la positionne sur le besoin
  // de la campagne — c'est la suite naturelle d'un profil retenu, là où le
  // vivier ne fait que le mettre de côté.
  const [besoinBusyId, setBesoinBusyId] = useState(null);
  const [addedIds, setAddedIds] = useState(() => new Set());
  // Repli : la fiche est créée par la route vivier (qui renvoie candidate_id),
  // puis positionnée sur le besoin par la route de positionnement classique.
  const ajoutEnDeuxTemps = useCallback(async (taskId, cand) => {
    if (besoinId == null) { showNotification?.('Besoin introuvable pour cette campagne.', 'error'); return false; }
    try {
      const rv = await fetch(`${BASE}/sourcing/${taskId}/vivier`, {
        method: 'POST', headers: jsonH(), body: JSON.stringify({ recruiterId: cand.recruiterId }),
      });
      const jv = await rv.json().catch(() => ({}));
      if (!rv.ok || jv.success === false) throw new Error(jv.error || jv.message || `HTTP ${rv.status}`);
      const candidateId = jv.candidate_id ?? jv.candidateId ?? jv.data?.candidate_id ?? null;
      if (candidateId == null) throw new Error('le serveur n\'a pas renvoyé l\'identifiant de la fiche créée');
      const rp = await fetch(`${BASE}/besoins/${besoinId}/candidats`, {
        method: 'POST', headers: jsonH(), body: JSON.stringify({ candidateId }),
      });
      if (!rp.ok) {
        const jp = await rp.json().catch(() => ({}));
        throw new Error(jp.error || jp.message || `HTTP ${rp.status}`);
      }
      showNotification?.('Profil ajouté au besoin — sa fiche a été créée et figure aussi au vivier.', 'success');
      return true;
    } catch (e) { showNotification?.('Ajout au besoin impossible : ' + e.message, 'error'); return false; }
  }, [besoinId, showNotification]);

  const addToBesoin = useCallback(async (taskId, cand) => {
    if (!taskId || cand?.recruiterId == null) return;
    setBesoinBusyId(cand.recruiterId);
    try {
      const r = await fetch(`${BASE}/sourcing/${taskId}/besoin`, {
        method: 'POST', headers: jsonH(), body: JSON.stringify({ recruiterId: cand.recruiterId }),
      });
      // Route dédiée absente : on obtient le même résultat en deux temps avec
      // des routes qui existent — créer la fiche (route vivier), puis la
      // positionner sur le besoin. Le profil apparaît donc aussi au vivier,
      // ce que le message de succès dit.
      if (r.status === 404 || r.status === 405 || r.status === 501) {
        const ok = await ajoutEnDeuxTemps(taskId, cand);
        if (ok) setAddedIds(prev => new Set(prev).add(cand.recruiterId));
        return;
      }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      showNotification?.(j.message || (j.already ? 'Ce profil est déjà positionné sur le besoin.' : 'Profil ajouté au besoin.'), 'success');
      setAddedIds(prev => new Set(prev).add(cand.recruiterId));
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
    finally { setBesoinBusyId(null); }
  }, [showNotification, ajoutEnDeuxTemps]);

  // task_id garanti sur chaque tâche du GET (contrat back) ; repli 202 par sûreté.
  const searchTaskId = search?.task_id ?? lastTaskId;

  return { tasks, loading, launching, sending, generating, launch, sendInmails, generateQuery, launchInfo, reload: load, showNotification,
    search, searchTaskId, inmail, sentNames, keepInVivier, vivierBusyId, keptIds,
    addToBesoin, besoinBusyId, addedIds, enabled, direct };
}

// ── Hook : conversations InMail (Erpia Agent) — route à venir côté back ──────
// GET /sourcing/:besoinId/conversations ; 404 → carte masquée (convos = null).
function useConversations(besoinId, { showNotification, watch = false } = {}) {
  const [convos, setConvos] = useState(null); // null = route absente / pas encore su
  const [creatingId, setCreatingId] = useState(null);
  const pollBusy = useRef(false);

  const load = useCallback(async () => {
    if (besoinId == null) return;
    try {
      const r = await fetch(`${BASE}/sourcing/${besoinId}/conversations`, { headers: authH() });
      if (r.status === 404) { setConvos(null); return; }
      if (r.ok) {
        const j = await r.json().catch(() => ({}));
        const arr = j.data || j.conversations || (Array.isArray(j) ? j : []);
        setConvos(Array.isArray(arr) ? arr : []);
      }
    } catch { /* offline → état courant conservé */ }
  }, [besoinId]);
  useEffect(() => { load(); }, [load]);

  // L'agent discute en asynchrone : léger poll 30 s tant qu'une discussion est
  // active — ou (watch) tant qu'on attend les premiers fils après des envois.
  const hasLive = (Array.isArray(convos) && convos.some(c => c.status === 'active' || c.status === 'waiting'))
    || (watch && Array.isArray(convos) && convos.length === 0);
  useEffect(() => {
    if (!hasLive) return undefined;
    const iv = setInterval(async () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (pollBusy.current) return;
      pollBusy.current = true;
      try { await load(); } finally { pollBusy.current = false; }
    }, 30000);
    return () => clearInterval(iv);
  }, [hasLive, load]);

  // POST create-candidate → { success, message, candidate_id, already } :
  // message prêt à l'emploi (toast tel quel), already:true = re-clic inoffensif.
  const createCandidate = useCallback(async (convo) => {
    const id = convo?.id ?? convo?.profileId;
    if (id == null) return;
    setCreatingId(id);
    try {
      const r = await fetch(`${BASE}/sourcing/conversations/${id}/create-candidate`, { method: 'POST', headers: jsonH() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(j.message || `${convo.name || 'Candidat'} créé dans le vivier — numéro et fil LinkedIn joints`, 'success');
      setConvos(prev => Array.isArray(prev) ? prev.map(c => ((c.id ?? c.profileId) === id
        ? { ...c, status: 'candidate_created', candidate_id: j.candidate_id ?? c.candidate_id } : c)) : prev);
      load();
    } catch (e) { showNotification?.('Erreur création candidat : ' + e.message, 'error'); }
    finally { setCreatingId(null); }
  }, [showNotification, load]);

  return { convos, reload: load, createCandidate, creatingId };
}

// ── Styles partagés ──────────────────────────────────────────────────────────
const INPUT = { width: '100%', boxSizing: 'border-box', border: '1px solid var(--input-border)', borderRadius: '8px', background: 'var(--input-bg)', padding: '10px 12px', fontFamily: 'inherit', fontSize: '13px', color: 'var(--text-strong)', outline: 'none' };
const GHOST_BTN = { display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', flex: 'none' };
const PRIMARY_BTN = { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 15px', borderRadius: '8px', border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)', fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, cursor: 'pointer', flex: 'none' };
const CARD = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px' };
const LBL = { display: 'block', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', marginBottom: '6px' };

// ── Formulaire de lancement (état « brouillon ») ─────────────────────────────
function LaunchForm({ besoin, sourcing, onLaunched, onCancel, profilRef }) {
  const { launching, generating, launch, generateQuery } = sourcing;
  // Deux façons de chercher : la requête booléenne (par défaut), ou un
  // PROFIL TYPE — Sourceur en dérive lui-même la requête « profils
  // similaires ». Arriver depuis un candidat arme directement ce mode.
  const [source, setSource] = useState(profilRef ? 'profil' : 'query');
  const [profil, setProfil] = useState(profilRef?.texte || '');
  // Choisir un candidat SANS repasser par « Nouvelle campagne » : le
  // sélecteur vit dans le formulaire et remplit le texte depuis la fiche.
  const [refNom, setRefNom] = useState(profilRef?.nom || null);
  const [candRefId, setCandRefId] = useState(profilRef?.id ?? null);
  // Le profil étalon : lu à fond par le moteur, son dossier repris ici.
  const [etalonId, setEtalonId] = useState(null);
  const [etalonOuvert, setEtalonOuvert] = useState(false);
  const [candOuvert, setCandOuvert] = useState(false);
  const [candQ2, setCandQ2] = useState('');
  const [cands2, setCands2] = useState(null);
  useEffect(() => {
    if (!candOuvert) return undefined;
    let annule = false;
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ page: '1', limit: '8' });
        if (candQ2.trim()) params.set('search', candQ2.trim());
        const r = await fetch(`${BASE}/candidats/vue?${params.toString()}`, { headers: authH() });
        const j = await r.json().catch(() => ({}));
        if (!annule) setCands2(Array.isArray(j.data) ? j.data : []);
      } catch { if (!annule) setCands2([]); }
    }, 250);
    return () => { annule = true; clearTimeout(t); };
  }, [candOuvert, candQ2]);
  const reprendreProfil = async (c) => {
    const nom = [c.prenom, c.nom].filter(Boolean).join(' ').trim() || `Candidat ${c.id}`;
    const texte = profilDe(c);
    // Ne jamais écraser en silence un texte déjà travaillé.
    if (profil.trim() && profil.trim() !== texte.trim()
      && !await confirmErpia({ message: `Remplacer le texte actuel par le profil de ${nom} ?` })) return;
    setProfil(texte);
    setRefNom(nom);
    setCandRefId(c.id);
    setEtalonId(null);
    setCandOuvert(false);
  };
  const [query, setQuery] = useState('');
  // La ville de la mission (rail « Détails » de la fiche), premier segment
  // (« Genève, Suisse » → « Genève ») — modifiable ici avant de lancer.
  const [ville, setVille] = useState(String(besoin?.localisation || besoin?.ville || '').split(/[,;/|]/)[0].trim());
  const [topN, setTopN] = useState(5);
  const [maxProfils, setMaxProfils] = useState(''); // '' = volume standard du moteur
  const [minScore, setMinScore] = useState(''); // vide = seuil par défaut du serveur
  const [experience, setExperience] = useState(''); // ex. "5+" — optionnel, l'IA le déduit sinon
  const [mode, setMode] = useState('validate');
  // Sur l'interface v2 (essai actif), le moteur version 2 est le défaut du
  // lancement — l'utilisateur peut toujours revenir en Standard.
  // Canaux : la liste vient du serveur (GET /sourcing/canaux) — on n'affiche
  // JAMAIS une case pour un canal que le moteur ne sait pas interroger.
  // undefined = pas encore su, null = route absente (l'écran reste utilisable,
  // la recherche part alors sur LinkedIn seul comme avant).
  const [canaux, setCanaux] = useState(undefined);
  const [canauxChoisis, setCanauxChoisis] = useState(() => new Set());
  // Jackpot : une techno ou un mot rare que le recruteur guette (« Regdata »).
  const [jackpot, setJackpot] = useState('');
  // Les mots rares que « Générer » suggère : proposés, jamais posés tout
  // seuls — le recruteur les ajoute d'un clic, ou les ignore.
  const [jackpotSuggere, setJackpotSuggere] = useState([]);
  // Mode jackpot : avec (la première passe cherche la requête ET l'un des
  // mots) ou sans (les mots sont seulement guettés).
  const [jackpotEnPremier, setJackpotEnPremier] = useState(true);
  const ajouterJackpot = (mot) => setJackpot(prev => {
    const deja = prev.split(/[,;\n]/).map(x => x.trim().toLowerCase()).filter(Boolean);
    if (deja.includes(String(mot).toLowerCase())) return prev;
    return prev.trim() ? `${prev.trim().replace(/,\s*$/, '')}, ${mot}` : mot;
  });
  // « Open to work seulement » : le réseau ciblé filtre la liste, le moteur
  // écarte ce qui ne l'affiche pas — moins de profils, joignables tout de suite.
  const [otwSeul, setOtwSeul] = useState(false);
  const termesJackpot = () => jackpot.split(/[,;\n]/).map(x => x.trim()).filter(Boolean);
  // V2.1 — Entreprises visées : les sociétés du même domaine que le client
  // où piocher (« Skysoft » → Dassault, Indra). « Générer » en suggère ;
  // proposées, jamais posées toutes seules — même règle que le jackpot.
  const [entreprises, setEntreprises] = useState('');
  const [entreprisesSuggere, setEntreprisesSuggere] = useState([]);
  // Langues requises par le client (« Français, Anglais ») — par défaut celles
  // du besoin. Un retenu qui ne les déclare pas sur son profil est signalé.
  const [langues, setLangues] = useState(String(besoin?.langues || '').trim());
  const termesLangues = () => langues.split(/[,;\n/]/).map(x => x.trim()).filter(Boolean);
  const ajouterEntreprise = (nom) => setEntreprises(prev => {
    const deja = prev.split(/[,;\n]/).map(x => x.trim().toLowerCase()).filter(Boolean);
    if (deja.includes(String(nom).toLowerCase())) return prev;
    return prev.trim() ? `${prev.trim().replace(/,\s*$/, '')}, ${nom}` : nom;
  });
  const termesEntreprises = () => entreprises.split(/[,;\n]/).map(x => x.trim()).filter(Boolean);
  // Pays de recherche : où chercher les profils, indépendamment de la ville
  // du poste (« le poste est à Genève, les bons profils sont en Espagne »).
  const [pays, setPays] = useState('');
  const termesPays = () => pays.split(/[,;\n]/).map(x => x.trim()).filter(Boolean);
  useEffect(() => {
    let annule = false;
    (async () => {
      try {
        const r = await fetch(`${BASE}/sourcing/canaux`, { headers: authH() });
        if (r.status === 404 || r.status === 405 || r.status === 501) { if (!annule) setCanaux(null); return; }
        const j = await r.json().catch(() => ({}));
        if (annule || j.success === false) return;
        const liste = Array.isArray(j.canaux) ? j.canaux : [];
        setCanaux(liste);
        // Par défaut : tout ce que le moteur couvre. Chercher large est le
        // comportement attendu ; on décoche pour restreindre, pas l'inverse.
        setCanauxChoisis(new Set(liste.filter(c => c.couvert && !c.socle).map(c => c.code)));
      } catch { if (!annule) setCanaux(null); }
    })();
    return () => { annule = true; };
  }, []);
  const basculerCanal = (code) => setCanauxChoisis(prev => {
    const s2 = new Set(prev);
    if (s2.has(code)) s2.delete(code); else s2.add(code);
    return s2;
  });
  const [shareJd, setShareJd] = useState(false); // partager la JD publique au candidat qui répond
  const [aiNote, setAiNote] = useState('');
  // Entreprises à ne pas contacter : chips cochables. kind 'client' (pré-cochée),
  // 'sugg' (suggestion décochée : éditeur/partenaire du mandat), 'custom' (saisie
  // libre, supprimable). Aucune chip = champ non envoyé → le back exclut le client.
  const [exclusions, setExclusions] = useState([]); // [{ name, checked, kind }]
  const [exclInput, setExclInput] = useState('');
  // Qui prévenir : le réglage du besoin sert de base ; l'ajustement ne vaut
  // que pour cette campagne et ne touche pas au défaut.
  const { ids: notifiesDefaut, absent: defautsAbsents } = useNotifiesBesoin(besoin?.id);
  const [notifies, setNotifies] = useState([]);
  const notifiesTouche = useRef(false);
  useEffect(() => {
    if (!notifiesTouche.current && Array.isArray(notifiesDefaut)) setNotifies(notifiesDefaut);
  }, [notifiesDefaut]);

  const addExclusion = (raw) => {
    const name = raw.trim();
    if (!name) return;
    setExclusions(prev => prev.some(e => e.name.toLowerCase() === name.toLowerCase())
      ? prev : [...prev, { name, checked: true, kind: 'custom' }]);
    setExclInput('');
  };
  const toggleExclusion = (name) => setExclusions(prev => prev.map(e => e.name === name ? { ...e, checked: !e.checked } : e));
  const removeExclusion = (name) => setExclusions(prev => prev.filter(e => e.name !== name));

  const onGenerate = async () => {
    const j = await generateQuery({ ville });
    if (!j) return;
    setQuery(j.query || '');
    if (!ville.trim() && j.ville) setVille(j.ville);
    setAiNote(j.explication || '');
    // Les mots rares que la génération suggère sont PROPOSÉS sous le champ,
    // jamais posés tout seuls : une campagne partait avec des mots que le
    // recruteur n'avait pas demandés, cherchés en premier par le moteur.
    setJackpotSuggere(Array.isArray(j.jackpot_suggere) ? j.jackpot_suggere.filter(Boolean) : []);
    // Les sociétés du même domaine que le client, où piocher : proposées
    // sous le champ, ajoutées d'un clic — jamais posées toutes seules.
    setEntreprisesSuggere(Array.isArray(j.entreprises_suggerees) ? j.entreprises_suggerees.filter(Boolean) : []);
    // Le générateur avait mis la société visée dans la requête : le serveur
    // l'a retirée, la note de génération le dit.
    if (Array.isArray(j.retires_de_la_requete) && j.retires_de_la_requete.length) setAiNote(`${j.explication || ''} — « ${j.retires_de_la_requete.join(' », « ')} » retiré de la requête : c'est la société visée, on cherche des candidats pour elle, pas chez elle.`.replace(/^ — /, ''));
    // Exclusions du preview : client pré-coché, suggestions décochées —
    // en conservant les états déjà togglés et les saisies libres.
    const fromBack = [
      ...(Array.isArray(j.exclusions_client) ? j.exclusions_client : []).map(n => ({ name: n, checked: true, kind: 'client' })),
      ...(Array.isArray(j.exclusions_suggerees) ? j.exclusions_suggerees : []).map(n => ({ name: n, checked: false, kind: 'sugg' })),
    ];
    setExclusions(prev => {
      const prevByName = new Map(prev.map(e => [e.name.toLowerCase(), e]));
      const merged = fromBack.map(e => prevByName.get(e.name.toLowerCase()) ?? e);
      const customs = prev.filter(e => e.kind === 'custom' && !merged.some(m => m.name.toLowerCase() === e.name.toLowerCase()));
      return [...merged, ...customs];
    });
  };

  const seg = (on) => ({ padding: '6px 13px', borderRadius: '99px', border: 'none', background: on ? 'var(--accent)' : 'transparent', color: on ? 'var(--accent-fg)' : 'var(--text-2)', fontFamily: 'inherit', fontSize: '12px', fontWeight: on ? 600 : 500, cursor: 'pointer', whiteSpace: 'nowrap' });

  return (
    <div style={{ ...CARD, marginTop: '16px', padding: '18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ flex: 1, fontSize: '14px', fontWeight: 700, color: 'var(--text-strong)' }}>Recherche LinkedIn</span>
        {source === 'query' && <button onClick={onGenerate} disabled={generating || launching} className="src-row-hover" style={GHOST_BTN}
          title="L'IA propose une query booléenne à partir du besoin — retouchez avant de lancer.">
          <BI icon={generating ? 'arrow-repeat' : 'stars'} style={generating ? { animation: 'ats-spin .7s linear infinite' } : undefined} /> {generating ? 'Génération…' : 'Générer avec l’IA'}
        </button>}
      </div>
      <div style={{ marginTop: '11px', display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '3px', borderRadius: '99px', background: 'var(--code-bg)', border: '1px solid var(--border)' }}>
        <button type="button" onClick={() => setSource('query')} style={seg(source === 'query')}>Requête booléenne</button>
        <button type="button" onClick={() => setSource('profil')} style={seg(source === 'profil')}>Profil type</button>
      </div>

      {source === 'profil' ? (
        <>
          <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <button type="button" onClick={() => setCandOuvert(v => !v)} className="src-row-hover" style={GHOST_BTN}>
              <BI icon={candOuvert ? 'chevron-up' : 'person-badge'} /> {candOuvert ? 'Fermer' : 'Choisir un candidat du vivier'}
            </button>
            {refNom && <span style={{ fontSize: '12px', color: 'var(--text-2)' }}>Profil repris de <strong style={{ color: 'var(--text-strong)' }}>{refNom}</strong></span>}
            {/* Le profil étalon : le moteur lit la fiche (ou le texte) en
                entier, rend le dossier, et le formulaire le reprend — avec
                les mots rares en jackpot. */}
            <button type="button" onClick={() => setEtalonOuvert(true)} disabled={!candRefId && !profil.trim()} className="src-row-hover"
              style={{ ...GHOST_BTN, opacity: !candRefId && !profil.trim() ? 0.55 : 1 }}
              title="Le moteur lit ce profil en entier : résumé, points forts, compétences clés, mots rares, critères du clone.">
              <BI icon="search-heart" /> {etalonId ? 'Relire à fond' : 'Analyser à fond'}
            </button>
            {etalonId && <Chip kind="warn" style={{ fontSize: '11px' }}><BI icon="people" /> clone de {refNom || 'ce profil'}</Chip>}
            <span style={{ flex: 1, minWidth: '160px', fontSize: '11.5px', color: 'var(--text-3)' }}>…ou écrivez le profil vous-même ci-dessous.</span>
          </div>
          {etalonOuvert && (
            <ProfilEtalon besoin={besoin} showNotification={sourcing.showNotification}
              cible={candRefId ? { candidate_id: candRefId, nom: refNom || undefined } : { texte: profil, nom: refNom || undefined }}
              onFermer={() => setEtalonOuvert(false)}
              onLance={() => { setEtalonOuvert(false); onLaunched?.(); }}
              onUtiliser={(et) => {
                setProfil(texteDossier(et.dossier));
                if (et.nom) setRefNom(et.nom);
                setEtalonId(et.id);
                const rares = Array.isArray(et.clone?.jackpot) && et.clone.jackpot.length ? et.clone.jackpot : (Array.isArray(et.dossier?.rares) ? et.dossier.rares : []);
                if (rares.length) setJackpot(rares.join(', '));
                const v = et.clone?.ville || et.dossier?.localisation;
                if (v && !/non pr[eé]cis|inconnu/i.test(v) && !ville.trim()) setVille(v);
                setEtalonOuvert(false);
              }} />
          )}
          {candOuvert && (
            <div style={{ marginTop: '8px', border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden' }}>
              <input autoFocus value={candQ2} onChange={e => setCandQ2(e.target.value)} placeholder="Rechercher un candidat — nom, poste…"
                style={{ ...INPUT, border: 'none', borderBottom: '1px solid var(--hair)', borderRadius: 0 }} />
              <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                {cands2 === null && <div style={{ padding: '12px 14px', fontSize: '12.5px', color: 'var(--text-faint)' }}>Chargement des candidats…</div>}
                {Array.isArray(cands2) && cands2.length === 0 && <div style={{ padding: '12px 14px', fontSize: '12.5px', color: 'var(--text-faint)' }}>Aucun candidat ne correspond.</div>}
                {(cands2 || []).map(c => {
                  const nom = [c.prenom, c.nom].filter(Boolean).join(' ').trim() || `Candidat ${c.id}`;
                  const vide = !profilDe(c);
                  return (
                    <div key={c.id} onClick={() => reprendreProfil(c)} className="src-row-hover"
                      style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderBottom: '1px solid var(--hair)', cursor: 'pointer' }}>
                      <BI icon="person" style={{ color: 'var(--text-3)', flex: 'none' }} />
                      <span style={{ flex: 1, minWidth: 0, fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {nom} {(c.titre || c.poste) && <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>· {c.titre || c.poste}</span>}
                      </span>
                      {vide && <Chip kind="warn" style={{ fontSize: '10.5px' }}>fiche peu remplie</Chip>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <textarea value={profil} onChange={e => setProfil(e.target.value)} rows={7}
            placeholder={'Senior DevOps Engineer — 12 ans d\'expérience\nCompétences : Kubernetes, Terraform, AWS, CI/CD…\nExpériences : …'}
            style={{ ...INPUT, marginTop: '10px', resize: 'vertical', fontSize: '12.5px', lineHeight: 1.6 }} />
          <div style={{ marginTop: '7px', fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.55 }}>
            {refNom
              ? <>Profil repris de <strong style={{ color: 'var(--text-strong)' }}>{refNom}</strong> — modifiable ici sans toucher à sa fiche. </>
              : 'Choisissez un candidat du vivier, ou collez un profil de référence (poste, compétences, expériences). '}
            Sourceur en dérive une recherche « profils similaires » ; le nom de la personne et son
            employeur actuel ne sont pas utilisés — on cherche des profils comparables, pas elle ni
            ses collègues.
          </div>
        </>
      ) : (
      <textarea value={query} onChange={e => setQuery(e.target.value)} rows={3}
        placeholder={'(ServiceNow AND "business analyst") AND (Genève OR Lausanne)'}
        style={{ ...INPUT, marginTop: '10px', resize: 'vertical', fontFamily: "'IBM Plex Mono',monospace", fontSize: '12.5px', lineHeight: 1.6 }} />
      )}
      {source === 'query' && aiNote && (
        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '9px 12px', borderRadius: '8px', background: 'var(--code-bg)' }}>
          <BI icon="info-circle" style={{ flex: 'none', marginTop: '1px', color: 'var(--text-2)', fontSize: '12px' }} />
          <span style={{ fontSize: '12px', lineHeight: 1.5, color: 'var(--text-2)' }}>{aiNote}</span>
        </div>
      )}
      <div style={{ marginTop: '13px', display: 'grid', gridTemplateColumns: '1fr 140px 110px 130px 130px', gap: '12px' }}>
        <div><label style={LBL}>Ville / région</label><input value={ville} onChange={e => setVille(e.target.value)} placeholder="Genève" style={INPUT} /></div>
        <div><label style={LBL} title="Optionnel — sans valeur et query générée par l'IA, le back le déduit du besoin.">Années d'exp. min.</label><input value={experience} onChange={e => setExperience(e.target.value)} placeholder="5+" style={{ ...INPUT, fontFamily: "'IBM Plex Mono',monospace" }} /></div>
        <div><label style={LBL}>Top N profils</label><input type="number" min={1} max={25} value={topN} onChange={e => setTopN(e.target.value)} style={{ ...INPUT, fontFamily: "'IBM Plex Mono',monospace" }} /></div>
        <div><label style={LBL} title="Sous ce score, le profil passe sous le seuil (repêchable au vivier). Vide = seuil par défaut.">Score minimum</label>
          <input type="number" min={0} max={100} value={minScore} onChange={e => setMinScore(e.target.value)} placeholder="défaut" style={{ ...INPUT, fontFamily: "'IBM Plex Mono',monospace" }} /></div>
        <div><label style={LBL} title="Combien de profils le réseau ciblé lit avant le tri. Plus il y en a, plus la lecture est longue : 200 profils prennent environ quatre fois le temps de 50.">Profils bruts</label>
          <select value={maxProfils} onChange={e => setMaxProfils(e.target.value)} style={{ ...INPUT, fontFamily: "'IBM Plex Mono',monospace" }}>
            <option value="">standard (≈ 50)</option><option value="100">100</option><option value="200">200</option><option value="400">400</option>
          </select></div>
      </div>
      {/* Entreprises à ne pas contacter (chips) */}
      <div style={{ marginTop: '13px' }}>
        <label style={LBL} title="Cochées = jamais contactées. Vide = le client du besoin est exclu automatiquement.">Entreprises à ne pas contacter</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap' }}>
          {exclusions.map(e => (
            <button key={e.name} type="button" onClick={() => toggleExclusion(e.name)}
              className={e.checked ? undefined : 'src-row-hover'}
              title={e.kind === 'sugg' ? 'Éditeur/partenaire du mandat — exclure ?' : e.kind === 'client' ? 'Client du besoin' : undefined}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '6px 13px', borderRadius: '99px', fontFamily: 'inherit', fontSize: '12px', cursor: 'pointer', userSelect: 'none',
                border: e.checked ? '1px solid transparent' : '1px solid var(--input-border)',
                background: e.checked ? 'var(--accent)' : 'var(--ghost-bg)',
                color: e.checked ? 'var(--accent-fg)' : 'var(--text-2)',
                fontWeight: e.checked ? 600 : 500 }}>
              <BI icon={e.checked ? 'check-lg' : 'plus-lg'} style={{ fontSize: '10px', opacity: e.checked ? 1 : 0.6 }} />
              {e.name}
              {e.kind === 'sugg' && !e.checked && <span style={{ fontWeight: 400, fontSize: '10.5px', color: 'var(--text-faint)', fontFamily: "'IBM Plex Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.04em' }}>suggéré</span>}
              {e.kind === 'custom' && (
                <span onClick={(ev) => { ev.stopPropagation(); removeExclusion(e.name); }} title="Retirer"
                  style={{ display: 'inline-flex', marginLeft: '1px', color: e.checked ? 'var(--accent-fg)' : 'var(--text-faint)', opacity: 0.7 }}><BI icon="x" style={{ fontSize: '12px' }} /></span>
              )}
            </button>
          ))}
          <input value={exclInput} onChange={e => setExclInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addExclusion(exclInput); } }}
            onBlur={() => addExclusion(exclInput)}
            placeholder={exclusions.length ? 'Ajouter…' : 'Nom d’entreprise (Entrée pour ajouter)'}
            style={{ ...INPUT, width: 'auto', flex: '1 1 180px', minWidth: '160px', padding: '7px 11px', fontSize: '12.5px' }} />
        </div>
        <div style={{ marginTop: '6px', fontSize: '11.5px', color: 'var(--text-3)' }}>
          {exclusions.length === 0
            ? 'Sans rien préciser, le client du besoin est exclu automatiquement. « Générer avec l’IA » propose aussi les partenaires du mandat.'
            : 'Les profils employés par une entreprise cochée sont écartés (visibles sous le seuil, repêchables au vivier).'}
        </div>
      </div>
      {/* Où chercher. LinkedIn est le socle (l'agent part de lui) ; les
          autres canaux sont interrogés par API en parallèle et fusionnés
          avant le classement — un seul palmarès, toutes sources confondues.
          Seuls les profils LinkedIn peuvent recevoir un InMail : ailleurs,
          la prise de contact est manuelle, et c'est dit ici. */}
      {Array.isArray(canaux) && canaux.some(c => c.couvert && !c.socle) && (
        <div style={{ marginTop: '13px' }}>
          <label style={LBL}>Où chercher</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px', marginTop: '3px' }}>
            {canaux.filter(c => c.couvert).map(c => {
              const coche = c.socle || canauxChoisis.has(c.code);
              return (
                <button key={c.code} type="button" title={c.detail ? `${c.note} ${c.detail}` : c.note}
                  onClick={c.socle ? undefined : () => basculerCanal(c.code)}
                  aria-pressed={c.socle ? undefined : coche}
                  disabled={c.socle}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '7px 12px', borderRadius: '8px',
                    border: `1px solid ${coche ? 'var(--accent)' : 'var(--input-border)'}`,
                    background: coche ? 'var(--accent-soft-bg, transparent)' : 'transparent',
                    color: coche ? 'var(--accent)' : 'var(--text-2)',
                    fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600,
                    cursor: c.socle ? 'default' : 'pointer' }}>
                  <BI icon={coche ? 'check-square' : 'square'} style={{ fontSize: '12px' }} />
                  {c.label}
                  {c.socle && <span style={{ fontSize: '10.5px', fontWeight: 500, opacity: 0.75 }}>toujours</span>}
                  {/* La santé côté moteur prime sur le constat : un canal sans
                      clé n'est pas « jamais utilisé », il est non configuré. */}
                  {!c.socle && c.configuree === false && <span style={{ fontSize: '10.5px', fontWeight: 600, color: 'var(--danger-fg)' }}>non configuré</span>}
                  {!c.socle && c.configuree !== false && c.degradee && <span style={{ fontSize: '10.5px', fontWeight: 500, color: 'var(--warn)' }}>mode réduit</span>}
                  {!c.socle && c.configuree !== false && !c.degradee && !c.constate && <span style={{ fontSize: '10.5px', fontWeight: 500, opacity: 0.7 }}>jamais utilisé</span>}
                </button>
              );
            })}
          </div>
          <div style={{ marginTop: '7px', fontSize: '12px', color: 'var(--text-3)', lineHeight: 1.5 }}>
            Les profils trouvés hors LinkedIn ne reçoivent pas d’InMail : vous les contactez vous-même, avec le lien de leur fiche.
            {canaux.some(c => c.couvert && !c.socle && c.configuree !== false && !c.degradee && !c.constate) && ' Un canal marqué « jamais utilisé » n’a encore ramené aucun profil ici — s’il reste vide, c’est en général qu’il n’est pas configuré côté moteur.'}
          </div>
          {/* Ce que le moteur dit de chaque canal qui ne peut pas répondre
              en entier : cocher un canal sans clé ne servirait à rien. */}
          {canaux.some(c => !c.socle && (c.configuree === false || c.degradee)) && (
            <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
              {canaux.filter(c => !c.socle && (c.configuree === false || c.degradee)).map(c => (
                <div key={c.code} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '11.5px', lineHeight: 1.45, color: c.configuree === false ? 'var(--danger-fg)' : 'var(--text-3)' }}>
                  <BI icon={c.configuree === false ? 'x-octagon' : 'exclamation-triangle'} style={{ fontSize: '10px', marginTop: '3px', flex: 'none' }} />
                  <span><strong>{c.label}</strong> — {c.detail}{c.reglage ? ` Réglage à poser côté moteur : ${c.reglage}.` : ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Jackpot : une techno ou un mot rare que le recruteur guette. Le
          moteur passe chaque profil au crible ; celui qui l'a est signalé
          JACKPOT — sur la carte, dans la sélection, par le copilote. */}
      <div style={{ marginTop: '13px' }}>
        <label style={LBL} htmlFor="src-jackpot">Jackpot</label>
        <input id="src-jackpot" style={INPUT} value={jackpot} onChange={e => setJackpot(e.target.value)} placeholder="Regdata, Avaloq, Temenos…" maxLength={300} />
        {jackpotSuggere.length > 0 && (
          <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-3)' }}>Suggérés par « Générer » — cliquez pour guetter :</span>
            {jackpotSuggere.map(m => (
              <button key={m} type="button" className="erp-chip" onClick={() => ajouterJackpot(m)} title="Ajouter ce mot au jackpot" style={{ cursor: 'pointer' }}>
                <BI icon="plus" style={{ fontSize: '10px' }} /> {m}
              </button>
            ))}
            <button type="button" className="spl-lien" onClick={() => setJackpotSuggere([])} style={{ fontSize: '11px' }}>Ignorer</button>
          </div>
        )}
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', fontSize: '12.5px', color: 'var(--text-2)', cursor: 'pointer' }}>
          <input type="checkbox" checked={jackpotEnPremier} onChange={e => setJackpotEnPremier(e.target.checked)} />
          <span><strong>Mode jackpot</strong> — {jackpotEnPremier ? 'la première passe cherche la requête ET l’un de ces mots ; si elle ne suffit pas, la suivante reprend la requête complète.' : 'les mots sont seulement guettés : la requête part telle quelle, les profils qui les portent sont signalés.'}</span>
        </label>
        <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--text-3)', lineHeight: 1.5 }}>
          Une techno ou un mot rare, séparés par des virgules. Le profil qui l’a est signalé « Jackpot » partout : sur la carte, dans la sélection, par le copilote. « Générer » en suggère quand il en trouve ; ils ne s’ajoutent que d’un clic. Le nom du client n’est jamais cherché : il est retiré d’office.
        </div>
      </div>

      {/* V2.1 — Entreprises visées : piocher chez les concurrents et les
          sociétés du même domaine que le client. Le moteur les cherche en
          première passe et marque chaque profil qui en vient. */}
      <div style={{ marginTop: '13px' }}>
        <label style={LBL} htmlFor="src-entreprises">Entreprises visées</label>
        <input id="src-entreprises" style={INPUT} value={entreprises} onChange={e => setEntreprises(e.target.value)} placeholder="Dassault Systèmes, Indra, Thales…" maxLength={600} />
        {entreprisesSuggere.length > 0 && (
          <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-3)' }}>Suggérées par « Générer » — même domaine que le client, cliquez pour y piocher :</span>
            {entreprisesSuggere.map(m => (
              <button key={m} type="button" className="erp-chip" onClick={() => ajouterEntreprise(m)} title="Ajouter cette entreprise aux entreprises visées" style={{ cursor: 'pointer' }}>
                <BI icon="plus" style={{ fontSize: '10px' }} /> {m}
              </button>
            ))}
            <button type="button" className="spl-lien" onClick={() => setEntreprisesSuggere([])} style={{ fontSize: '11px' }}>Ignorer</button>
          </div>
        )}
        <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--text-3)', lineHeight: 1.5 }}>
          Les sociétés où piocher, séparées par des virgules : concurrents et acteurs du même domaine que le client. La première passe cherche la requête ET l’une d’elles ; chaque profil qui y travaille ou y a travaillé est signalé « chez … » et passe en tête. Le client n’en fait jamais partie : il est retiré d’office.
        </div>
      </div>

      {/* Pays de recherche : où chercher les profils — la ville du poste reste
          la ville du poste (InMail, scoring). */}
      <div style={{ marginTop: '13px' }}>
        <label style={LBL} htmlFor="src-langues">Langues requises</label>
        <input id="src-langues" style={INPUT} value={langues} onChange={e => setLangues(e.target.value)} placeholder="Français, Anglais" maxLength={120} />
        <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--text-3)', lineHeight: 1.5 }}>
          Celles que le client attend. Un profil retenu qui ne les déclare pas est signalé sur sa ligne, jamais écarté : la section Langues d'un profil est souvent incomplète.
        </div>
      </div>
      <div style={{ marginTop: '13px' }}>
        <label style={LBL} htmlFor="src-pays">Pays de recherche</label>
        <input id="src-pays" style={INPUT} value={pays} onChange={e => setPays(e.target.value)} placeholder="Espagne, Portugal…" maxLength={200} />
        <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--text-3)', lineHeight: 1.5 }}>
          {pays.trim()
            ? <>Les profils sont cherchés en <strong>{termesPays().join(', ')}</strong> — filtre de lieu, recherche web et GitHub compris. Le poste reste à <strong>{ville.trim() || 'la ville du besoin'}</strong> : c’est elle que lisent l’InMail et le scoring, qui juge la mobilité.</>
            : <>Vide : les profils sont cherchés autour de la ville du poste. Renseignez un ou plusieurs pays quand les bons profils pour ce client sont ailleurs (« le poste est à Genève, mais la plupart sont en Espagne »).</>}
        </div>
      </div>

      {/* Open to work seulement : le réseau ciblé filtre lui-même la liste
          (y compris les candidats qui ne le montrent qu'aux recruteurs), le
          moteur écarte ce qui ne l'affiche pas, avec la raison sur chaque
          écarté. Un bon profil installé n'est pas retenu : c'est le but. */}
      <label style={{ marginTop: '13px', display: 'flex', alignItems: 'flex-start', gap: '9px', cursor: 'pointer' }}>
        <input type="checkbox" checked={otwSeul} onChange={e => setOtwSeul(e.target.checked)} style={{ marginTop: '3px' }} />
        <span>
          <span style={{ ...LBL, marginBottom: '2px' }}>Uniquement les profils « open to work »</span>
          <span style={{ display: 'block', fontSize: '12px', color: 'var(--text-3)', lineHeight: 1.5 }}>Le réseau ciblé filtre la liste sur les candidats ouverts aux opportunités, y compris ceux qui ne le montrent qu’aux recruteurs ; le moteur écarte ce qui ne l’affiche pas, en disant pourquoi. Moins de profils, mais joignables tout de suite.</span>
        </span>
      </label>

      <div style={{ marginTop: '13px' }}>
        <label style={LBL}>Envoi des InMails</label>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '3px', borderRadius: '99px', background: 'var(--code-bg)', border: '1px solid var(--border)' }}>
          <button type="button" onClick={() => setMode('validate')} style={seg(mode === 'validate')}>Validation manuelle</button>
          <button type="button" onClick={() => setMode('auto')} style={seg(mode === 'auto')}>Envoi automatique</button>
        </div>
        <div style={{ marginTop: '7px', fontSize: '12px', color: 'var(--text-3)' }}>
          {mode === 'auto'
            ? `Les InMails partent automatiquement aux Top ${topN || 'N'} dès la fin de la recherche — aucune validation intermédiaire.`
            : 'Vous validerez la sélection scorée avant tout envoi d’InMail.'}
        </div>
        <label style={{ marginTop: '10px', display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: '12.5px', color: 'var(--text-2)', cursor: 'pointer', userSelect: 'none' }}>
          <input type="checkbox" checked={shareJd} onChange={e => setShareJd(e.target.checked)} style={{ accentColor: 'var(--brand)' }} />
          Partager la JD si le candidat répond <span style={{ color: 'var(--text-faint)' }}>(lien public de la fiche de poste)</span>
        </label>
      </div>
      <NotifiesLancement defauts={notifiesDefaut} absent={defautsAbsents} valeur={notifies}
        onChange={(v) => { notifiesTouche.current = true; setNotifies(v); }} />
      <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px solid var(--hair)', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <span style={{ flex: 1, minWidth: '220px', fontSize: '12px', color: 'var(--text-3)' }}>{source === 'profil'
          ? 'La recherche booléenne est dérivée du profil par Sourceur ; elle sera affichée une fois construite.'
          : 'Champ de recherche vide ? L’IA générera la query booléenne au lancement, à partir du besoin.'}</span>
        {onCancel && <button onClick={onCancel} className="src-row-hover" style={GHOST_BTN}>Annuler</button>}
        <button onClick={async () => { if (source === 'profil' && !profil.trim()) { return; } if (await launch({ query: source === 'profil' ? '' : query, profile: source === 'profil' ? profil : '', candidatRefId: source === 'profil' ? candRefId : null, etalonId: source === 'profil' ? etalonId : null, ville, topN, minScore, mode, experience, shareJd, notifies, moteurV2: true, sources: [...canauxChoisis], jackpot: termesJackpot(), jackpotEnPremier, langues: termesLangues(), entreprises: termesEntreprises(), pays: termesPays(), openToWork: otwSeul, maxProfils, excludeCompanies: exclusions.length > 0 ? exclusions.filter(e => e.checked).map(e => e.name) : undefined })) onLaunched?.(); }} disabled={launching || (source === 'profil' && !profil.trim())}
          style={{ ...PRIMARY_BTN, cursor: launching ? 'not-allowed' : 'pointer', opacity: launching || (source === 'profil' && !profil.trim()) ? 0.6 : 1 }}>
          <BI icon={launching ? 'arrow-repeat' : 'search'} style={launching ? { animation: 'ats-spin .7s linear infinite' } : undefined} /> {launching ? 'Lancement…' : 'Lancer la recherche'}
        </button>
      </div>
    </div>
  );
}

// ── Panneau campagne d'un besoin (états : recherche / erreur / sélection /
// suivi / conversations) — le hook vit ici, un besoin = un état propre. ───────
// Noms des candidats déjà positionnés sur le besoin — sert à ne pas proposer
// un ajout qui existe déjà. Le rapprochement se fait par NOM (un résultat
// LinkedIn n'a pas d'identifiant de fiche tant qu'il n'est pas importé) :
// l'info-bulle le dit, pour qu'un homonyme reste compréhensible.
// Clé de rapprochement : accents retirés, mots TRIÉS. « Bourgues Nicolas »
// et « Nicolas Bourgues » donnent donc la même clé — l'ordre prénom/nom
// diffère d'une source à l'autre.
const clefNom = (s) => String(s || '').trim().toLowerCase()
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9\s]/g, ' ')
  .split(/\s+/).filter(Boolean).sort().join(' ');

// Un candidat peut être rattaché au besoin par deux chemins — le pipeline
// (lignes legacy) et les positionnements (fiches canoniques) — mais UNE seule
// route les rend : /candidats/besoin/:id sert leur UNION, dédoublonnée.
// On lisait en plus /besoins/:id/candidats, qui n'existe qu'en POST : chaque
// ouverture du panneau produisait un 404 silencieux (le catch ci-dessous
// l'avalait), et zéro nom en plus. Une source qui ne répond jamais ne protège
// de rien — elle fait seulement croire qu'on couvre les deux chemins.
function useDejaSurBesoin(besoinId) {
  const [noms, setNoms] = useState(() => new Set());
  useEffect(() => {
    if (besoinId == null) return undefined;
    let annule = false;
    (async () => {
      const trouve = new Set();
      const lire = async (url) => {
        try {
          const r = await fetch(url, { headers: authH() });
          if (!r.ok) return;
          const j = await r.json().catch(() => ({}));
          const arr = j?.data || j?.candidats || j?.candidates || (Array.isArray(j) ? j : []);
          if (!Array.isArray(arr)) return;
          arr.forEach(c => {
            const nom = [c.prenom, c.nom].filter(Boolean).join(' ').trim() || c.name || c.nom_complet || '';
            const k = clefNom(nom);
            if (k) trouve.add(k);
          });
        } catch { /* source indisponible : on ne bloque aucun ajout */ }
      };
      await lire(`${BASE}/candidats/besoin/${besoinId}`);
      if (!annule) setNoms(trouve);
    })();
    return () => { annule = true; };
  }, [besoinId]);
  return noms;
}

function CampaignPanel({ besoin, sourcing, showNotification, onEditSearch, onOpenCandidat }) {
  const dejaSurBesoin = useDejaSurBesoin(besoin?.id);
  const { search, searchTaskId, inmail, sending, sendInmails, launchInfo, launch, sentNames, keepInVivier, vivierBusyId, keptIds, addToBesoin, besoinBusyId, addedIds, direct } = sourcing;
  // ── Ce que voit la carte de ciblage ────────────────────────────────────
  // Un seul jeu de champs, deux sources : l'état fait foi dès qu'il porte
  // quelque chose (c'est le définitif), le direct comble pendant la chasse.
  const estV2 = search?.engine === 'v2';
  const v2Carte = useMemo(() => {
    if (!estV2) return null;
    const base = search?.v2 || { statut: 'en_cours', passes: [] };
    const passes = base.passes?.length ? base.passes : (direct?.passes || []);
    return {
      ...base, passes,
      seuil: base.seuil ?? direct?.seuil ?? null,
      budget: { ...(base.budget || {}),
        recherches_max: base.budget?.recherches_max ?? direct?.budget?.recherches_max ?? null,
        utilisees: passes.length },
    };
  }, [estV2, search?.v2, direct]);
  const candidatsCarte = (Array.isArray(search?.candidates) && search.candidates.length)
    ? search.candidates : (direct?.candidats || []);
  // Les gestes de la fiche : les MÊMES routes que la liste « Sélection
  // reçue ». Elles lisent la sélection stockée — donc tant qu'elle n'est pas
  // arrivée, la carte n'offre aucun bouton et le dit.
  // Le profil étalon d'un candidat trouvé : le moteur relit le profil brut
  // extrait, rend le dossier, et la chasse aux clones part de là.
  const [etalonCible, setEtalonCible] = useState(null);
  const ouvrirEtalon = useCallback((c) => {
    if (!searchTaskId || !c?.recruiterId) return;
    setEtalonCible({ task_id: searchTaskId, recruiter_id: c.recruiterId, nom: joinName(c.firstName, c.name), titre: c.titre || c.headline || null });
  }, [searchTaskId]);
  const gestesCarte = useMemo(() => ({
    pretes: search?.status === 'selection_received' && searchTaskId != null,
    onClone: ouvrirEtalon,
    etalonNom: search?.etalon?.nom || null,
    occupeId: besoinBusyId ?? vivierBusyId ?? null,
    ajoutes: addedIds, gardes: keptIds,
    onAjouterAuBesoin: (c) => addToBesoin(searchTaskId, c),
    onGarderAuVivier: (c) => keepInVivier(searchTaskId, c),
    onOuvrirFiche: onOpenCandidat ? (id) => onOpenCandidat(id) : undefined,
  }), [search?.status, search?.etalon?.nom, searchTaskId, besoinBusyId, vivierBusyId, addedIds, keptIds, addToBesoin, keepInVivier, onOpenCandidat, ouvrirEtalon]);
  const conv = useConversations(besoin?.id, {
    showNotification,
    // Des InMails sont partis → on guette l'arrivée des premiers fils.
    watch: (sourcing.inmail?.sent ?? 0) > 0 || sourcing.sentNames.length > 0,
  });
  const [checked, setChecked] = useState(() => new Set());
  const [openConvo, setOpenConvo] = useState(null);
  const [showRejected, setShowRejected] = useState(false);

  // Deux destinations distinctes : le BESOIN (on avance sur ce mandat) ou le
  // VIVIER (on met de côté pour plus tard — utile sous le seuil).
  const BesoinBtn = ({ c }) => {
    const fait = addedIds.has(c.recruiterId);
    const deja = !fait && dejaSurBesoin.has(clefNom(joinName(c.firstName, c.name)));
    const busy = besoinBusyId === c.recruiterId;
    if (fait) return <Chip kind="ok" style={{ fontSize: '11px' }}><BI icon="check-lg" /> Au besoin</Chip>;
    // Déjà positionné : on n'offre pas une action qui ne ferait rien.
    if (deja) return <Chip kind="mut" style={{ fontSize: '11px' }} title="Un candidat de ce nom est déjà positionné sur ce besoin."><BI icon="check-lg" /> Déjà au besoin</Chip>;
    return (
      <button onClick={(e) => { e.stopPropagation(); addToBesoin(searchTaskId, c); }} disabled={busy || !searchTaskId}
        className="src-row-hover" style={{ ...GHOST_BTN, padding: '5px 10px', fontSize: '11.5px', borderColor: 'var(--accent)', color: 'var(--accent)', opacity: busy ? 0.6 : 1 }}
        title="Crée la fiche candidat et la positionne sur le besoin de cette campagne.">
        <BI icon={busy ? 'arrow-repeat' : 'plus-lg'} style={busy ? { animation: 'ats-spin .7s linear infinite', fontSize: '11px' } : { fontSize: '11px' }} /> Ajouter au besoin
      </button>
    );
  };

  // « Garder au vivier » — sur les retenus comme sur les profils sous le seuil.
  const VivierBtn = ({ c }) => {
    const kept = keptIds.has(c.recruiterId);
    const busy = vivierBusyId === c.recruiterId;
    if (kept) return <Chip kind="ok" style={{ fontSize: '11px' }}><BI icon="check-lg" /> Au vivier</Chip>;
    return (
      <button onClick={(e) => { e.stopPropagation(); keepInVivier(searchTaskId, c); }} disabled={busy || !searchTaskId}
        className="src-row-hover" style={{ ...GHOST_BTN, padding: '5px 10px', fontSize: '11.5px', opacity: busy ? 0.6 : 1 }}
        title="Crée la fiche dans le vivier RH (colonne par défaut).">
        <BI icon={busy ? 'arrow-repeat' : 'person-plus'} style={busy ? { animation: 'ats-spin .7s linear infinite', fontSize: '11px' } : { fontSize: '11px' }} /> Garder au vivier
      </button>
    );
  };

  // « Trouver son clone » : le client aime ce profil — on le lit à fond et on
  // cherche ses semblables. Disponible dès que la recherche existe.
  const CloneBtn = ({ c }) => (
    <button onClick={(e) => { e.stopPropagation(); ouvrirEtalon(c); }} disabled={!searchTaskId}
      className="src-row-hover" style={{ ...GHOST_BTN, padding: '5px 10px', fontSize: '11.5px' }}
      title="Le moteur lit ce profil en entier (résumé, points forts, mots rares) et prépare la recherche de ses clones.">
      <BI icon="people" style={{ fontSize: '11px' }} /> Trouver son clone
    </button>
  );

  const autoMode = search?.mode === 'auto';
  const candidates = useMemo(
    () => ((search?.status === 'selection_received' && Array.isArray(search.candidates)) ? search.candidates : []),
    [search],
  );
  const query = search?.query || launchInfo?.query || '';
  const aiGen = !!(search?.query_generee ?? launchInfo?.query_generee);
  // Recherche partie d'un profil type : la query affichée est celle que
  // Sourceur a DÉRIVÉE du profil, et le raisonnement dit pourquoi. Les deux
  // viennent du serveur — rien n'est reconstitué ici.
  const depuisProfil = !!(search?.profil ?? launchInfo?.depuisProfil);
  const raisonnement = search?.profil_raisonnement || launchInfo?.explication || '';

  // Au retour de recherche (mode manuel) : profils ≥ 75 pré-cochés.
  const preDone = useRef(null);
  useEffect(() => {
    const key = search?.task_id;
    if (!autoMode && candidates.length > 0 && !inmail && preDone.current !== key) {
      preDone.current = key;
      setChecked(new Set(candidates.filter(c => Math.round(Number(c.score)) >= 75).map(c => c.recruiterId)));
    }
  }, [candidates, autoMode, inmail, search]);

  const toggle = (id) => setChecked(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selected = candidates.filter(c => checked.has(c.recruiterId));

  // Suivi des envois — par priorité : rapport du back (inmails[]), sinon file
  // optimiste du 202 (l'écran ne doit JAMAIS être vide après un envoi), sinon
  // (auto, rapport pas encore arrivé) la sélection en « file d'attente ».
  const entries = Array.isArray(inmail?.inmails) ? inmail.inmails : null;
  let sendRows;
  if (entries && entries.length > 0) sendRows = entries.map(m => ({ name: m.name, st: m.status === 'sent' ? 'ok' : m.status === 'error' ? 'fail' : 'pending', err: m.detail || '' }));
  else if (sentNames.length > 0) sendRows = sentNames.map(n => ({ name: n, st: 'pending', err: '' }));
  else if (autoMode && candidates.length > 0 && !inmail) sendRows = candidates.map(c => ({ name: joinName(c.firstName, c.name), st: 'pending', err: '' }));
  else sendRows = [];
  // Tâche inmail présente mais rapport vide et pas de file locale (reload
  // entre l'envoi et le rapport) → carte « rapport en attente », pas un vide.
  const awaitingReport = !!inmail && sendRows.length === 0 && (inmail.sent ?? 0) < (inmail.total ?? 0);
  const sentN = inmail?.sent ?? sendRows.filter(r => r.st === 'ok').length;
  const totalN = inmail?.total ?? sendRows.length;
  const showTracking = sendRows.length > 0 || awaitingReport || (!!inmail && (inmail.sent != null || inmail.total != null));
  const showSelection = !autoMode && candidates.length > 0 && !inmail && sentNames.length === 0;

  // Conversations (agent IA) — carte masquée tant que la route/les fils n'existent pas.
  // Statuts, téléphone, dispo et relances viennent du back tels quels.
  const convosTous = Array.isArray(conv.convos) ? conv.convos : [];
  // Les fils de CETTE campagne. Ceux des campagnes précédentes du besoin
  // restent lisibles — un candidat qui a répondu en août est toujours une
  // piste — mais repliés, et nommés pour ce qu'ils sont. Un fil sans
  // recherche connue n'est jamais attribué à la campagne en cours.
  const convos = convosTous.filter(cv => cv?.search_task_id != null && searchTaskId != null && Number(cv.search_task_id) === Number(searchTaskId));
  const convosAvant = convosTous.filter(cv => !convos.includes(cv));
  const [voirAvant, setVoirAvant] = useState(false);
  const lignesConvos = voirAvant ? [...convos, ...convosAvant] : convos;
  // Pilotage : cibles de défilement, candidats positionnés, pause/reprise,
  // et une relance vraiment « telle quelle » — moteur, expérience,
  // exclusions et canaux compris (elle les perdait, donc mentait).
  const refSelection = useRef(null), refInmails = useRef(null), refConvos = useRef(null);
  const defiler = (r) => r.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const positionnes = candidates.filter(c => c.sur_besoin === true || addedIds.has(c.recruiterId)).length;
  const [pauseEnCours, setPauseEnCours] = useState(false);
  // La recherche tourne chez le moteur : ERPIA relaie. Un 501 est une réponse
  // honnête (« pas encore disponible ») : affichée telle quelle, rien ne
  // change à l'écran ; l'état en_pause arrivera par le polling.
  const basculerPause = useCallback(async (action) => {
    if (besoin?.id == null || pauseEnCours) return;
    setPauseEnCours(true);
    try {
      const r = await fetch(`${BASE}/besoins/${besoin.id}/sourcing/${action}`, { method: 'POST', headers: jsonH() });
      const j = await r.json().catch(() => ({}));
      if (r.status === 501 || r.status === 404 || r.status === 405) { showNotification?.(j.error || j.message || 'Pas encore disponible côté moteur — la recherche continue.', 'error'); return; }
      if (!r.ok || j.success === false) { showNotification?.(j.error || j.message || `HTTP ${r.status}`, 'error'); return; }
      showNotification?.(j.message || (action === 'pause' ? 'Demande de pause transmise — l’état se mettra à jour dès confirmation.' : 'Demande de reprise transmise — l’état se mettra à jour dès confirmation.'), 'success');
      sourcing.reload?.();
    } catch (e) { showNotification?.(`Demande impossible : ${e.message}`, 'error'); }
    finally { setPauseEnCours(false); }
  }, [besoin?.id, pauseEnCours, showNotification, sourcing]);
  // Élargir d'un cran une campagne v2 arrêtée — même route que le tool agent
  // sourcing_elargir. Le moteur crée la passe suivante ; la campagne repasse
  // « en cours » jusqu'à la nouvelle sélection.
  const [elargirEnCours, setElargirEnCours] = useState(false);
  const elargir = useCallback(async (levier, niveau) => {
    if (besoin?.id == null || elargirEnCours) return;
    setElargirEnCours(true);
    try {
      const r = await fetch(`${BASE}/besoins/${besoin.id}/sourcing/elargir`, { method: 'POST', headers: jsonH(), body: JSON.stringify({ levier, niveau: niveau || undefined, task_id: searchTaskId ?? undefined }) });
      const j = await r.json().catch(() => ({}));
      if (r.status === 501 || r.status === 404 || r.status === 405) { showNotification?.(j.error || 'L’élargissement à la main arrive côté serveur.', 'error'); return; }
      if (!r.ok || j.success === false) { showNotification?.(j.error || j.message || `HTTP ${r.status}`, 'error'); return; }
      showNotification?.(j.message || 'Passe suivante lancée.', 'success');
      sourcing.reload?.();
    } catch (e) { showNotification?.(`Élargissement impossible : ${e.message}`, 'error'); }
    finally { setElargirEnCours(false); }
  }, [besoin?.id, elargirEnCours, searchTaskId, showNotification, sourcing]);
  // Vérifier « open to work » : l'agent ouvre chaque profil au statut inconnu
  // et lit le badge — aucun message au candidat. Même route que le tool
  // sourcing_open_to_work_verifier. Le résultat revient par le serveur
  // (webhook), donc on recharge après un délai.
  // « Demander son n° » : un InMail pour CE profil, objet 'numero' — le
  // message présente l'opportunité et demande le téléphone pour en parler de
  // vive voix. Le serveur refuse si le numéro est déjà connu (il le donne)
  // ou si la fiche est marquée « ne pas contacter » ; son message fait foi.
  const [numeroEnCours, setNumeroEnCours] = useState(null);
  // « Numéro demandé » : les tâches InMail d'objet « numero » de CETTE
  // recherche disent à qui on l'a demandé et où ça en est (en file, envoyé,
  // en erreur) — le serveur les porte dès l'envoi. La file locale couvre la
  // seconde entre le clic et la relecture.
  const [numeroLocal, setNumeroLocal] = useState(() => new Set());
  const numeroDemandes = useMemo(() => {
    const m = new Map();
    for (const t of (sourcing.tasks || [])) {
      if (t?.kind !== 'inmail' || t.objet !== 'numero' || !search || !deCetteRecherche(t, search)) continue;
      for (const e of (Array.isArray(t.inmails) ? t.inmails : [])) {
        const rid = String(e?.recruiterId || ''); if (!rid) continue;
        const statut = e.status === 'sent' ? 'envoye' : e.status === 'error' ? 'erreur' : 'en_file';
        const prev = m.get(rid);
        if (!prev || (prev.statut !== 'envoye' && statut === 'envoye')) m.set(rid, { statut, le: t.updated_at || t.created_at || null, detail: e.detail || '' });
      }
    }
    for (const rid of numeroLocal) if (!m.has(rid)) m.set(rid, { statut: 'en_file', le: null, detail: '' });
    return m;
  }, [sourcing.tasks, search, numeroLocal]);
  // « Retirer de la campagne » / « Remettre » : à partir du retrait, plus
  // rien ne part vers ce candidat automatiquement pour ce besoin. Même route
  // que le tool du copilote ; le serveur applique la barrière partout.
  const [retraitEnCours, setRetraitEnCours] = useState(null);
  const retirerCandidat = useCallback(async (c, remettre = false) => {
    if (besoin?.id == null || !c?.recruiterId || retraitEnCours) return;
    setRetraitEnCours(c.recruiterId);
    try {
      const r = await fetch(`${BASE}/besoins/${besoin.id}/sourcing/candidats/${remettre ? 'remettre' : 'retirer'}`, {
        method: 'POST', headers: jsonH(), body: JSON.stringify({ recruiter_id: c.recruiterId, nom: joinName(c.firstName, c.name) || c.name }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 404 || r.status === 501 || r.status === 405) { showNotification?.(j.error || 'Le retrait de la campagne arrive côté serveur.', 'info'); return; }
      if (!r.ok || j.success === false) { showNotification?.(j.error || `HTTP ${r.status}`, 'error'); return; }
      showNotification?.(j.message || (remettre ? 'Candidat remis dans la campagne.' : 'Candidat retiré de la campagne.'), 'success');
      sourcing.reload?.();
    } catch (e) { showNotification?.(`${remettre ? 'Remise' : 'Retrait'} impossible : ${e.message}`, 'error'); }
    finally { setRetraitEnCours(null); }
  }, [besoin?.id, retraitEnCours, showNotification, sourcing]);
  // « Liste noire » : mettre un profil de la campagne sur la liste noire de
  // l'organisation — même sans fiche (le serveur la retrouve ou la crée, sans
  // le positionner). Motif obligatoire, demandé par la modale ; le message
  // serveur fait foi. La ligne relit `ne_pas_contacter` au rechargement.
  const [listeNoireProfil, setListeNoireProfil] = useState(null);
  const [listeNoireEnCours, setListeNoireEnCours] = useState(null);
  const blacklister = useCallback(async (c, motif) => {
    if (!c?.recruiterId || listeNoireEnCours) return;
    setListeNoireEnCours(c.recruiterId);
    try {
      const r = await ajouterListeNoire({ motif, origine: 'sourceur', profil: { recruiterId: c.recruiterId, name: c.name, firstName: c.firstName, publicUrl: c.publicUrl, link: c.link, email: c.email, phone: c.phone || c.telephone } });
      showNotification?.(r.ok ? r.message : r.error, r.ok ? 'success' : (r.absente ? 'info' : 'error'));
      if (r.ok) { setListeNoireProfil(null); sourcing.reload?.(); }
    } finally { setListeNoireEnCours(null); }
  }, [listeNoireEnCours, showNotification, sourcing]);
  // « Proposer à une recruteuse » : un paquet de profils de la campagne part
  // chez une personne qui le trie (écran « Profils proposés »). Le serveur
  // dit ce qui ne part pas ; son message fait foi.
  const [proposerOuvert, setProposerOuvert] = useState(false);
  const [proposerEnCours, setProposerEnCours] = useState(false);
  const proposer = useCallback(async (recruiterIds, proposeA, note) => {
    if (!searchTaskId || proposerEnCours) return;
    setProposerEnCours(true);
    try {
      const r = await proposerProfils(searchTaskId, { recruiterIds, proposeA: Number(proposeA), note });
      showNotification?.(r.ok ? r.message : r.error, r.ok ? 'success' : (r.absente ? 'info' : 'error'));
      if (r.ok) { setProposerOuvert(false); sourcing.reload?.(); }
    } finally { setProposerEnCours(false); }
  }, [searchTaskId, proposerEnCours, showNotification, sourcing]);
  // « Liste 2 » : les profils mis de côté sur la campagne — liste serveur,
  // partagée par l'équipe, relue à chaque changement de campagne. Même route
  // que le tool sourcing_candidat_liste2 ; le serveur photographie la ligne.
  const [liste2, setListe2] = useState([]);
  const [liste2Dispo, setListe2Dispo] = useState(true);
  const [liste2EnCours, setListe2EnCours] = useState(null);
  const chargerListe2 = useCallback(async () => {
    if (besoin?.id == null) { setListe2([]); return; }
    try {
      const r = await fetch(`${BASE}/besoins/${besoin.id}/sourcing/liste2`, { headers: authH() });
      if (r.status === 404 || r.status === 501 || r.status === 405) { setListe2Dispo(false); return; }
      const j = await r.json().catch(() => ({}));
      if (r.ok && j.success !== false) { setListe2Dispo(true); setListe2(Array.isArray(j.liste) ? j.liste : []); }
    } catch { /* la carte le dit */ }
  }, [besoin?.id]);
  useEffect(() => { chargerListe2(); }, [chargerListe2, searchTaskId, search?.status]);
  const basculerListe2 = useCallback(async (c, retirer = false) => {
    if (besoin?.id == null || !c?.recruiterId || liste2EnCours) return;
    setListe2EnCours(c.recruiterId);
    try {
      const r = await fetch(`${BASE}/besoins/${besoin.id}/sourcing/candidats/liste2${retirer ? '/retirer' : ''}`, {
        method: 'POST', headers: jsonH(),
        body: JSON.stringify({ recruiter_id: c.recruiterId, nom: joinName(c.firstName, c.name) || c.name,
          ...(retirer ? {} : { profil: { name: c.name, firstName: c.firstName, headline: c.headline || c.role || c.titre, source: c.source, link: c.link, publicUrl: c.publicUrl, score: c.score, location: c.location, forts: c.forts, faibles: c.faibles, manque: c.manque, raisons: c.raisons } }) }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 404 || r.status === 501 || r.status === 405) { setListe2Dispo(false); showNotification?.(j.error || 'La liste 2 arrive côté serveur.', 'info'); return; }
      if (!r.ok || j.success === false) { showNotification?.(j.error || `HTTP ${r.status}`, 'error'); return; }
      showNotification?.(j.message || (retirer ? 'Profil sorti de la liste 2.' : 'Profil dans la liste 2.'), 'success');
      await chargerListe2();
      sourcing.reload?.();
    } catch (e) { showNotification?.(`Liste 2 : ${e.message}`, 'error'); }
    finally { setListe2EnCours(null); }
  }, [besoin?.id, liste2EnCours, showNotification, sourcing, chargerListe2]);
  const demanderNumero = useCallback(async (c) => {
    if (!searchTaskId || !c?.recruiterId) return;
    setNumeroEnCours(c.recruiterId);
    try {
      const r = await fetch(`${BASE}/sourcing/${searchTaskId}/inmail`, {
        method: 'POST', headers: jsonH(),
        body: JSON.stringify({ candidates: [{ recruiterId: c.recruiterId, name: c.name, firstName: c.firstName }], objet: 'numero' }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 404 && !j.error) throw new Error('la demande de numéro arrive côté serveur');
      if (!(r.status === 202 || r.ok) || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setNumeroLocal(s => { const n = new Set(s); n.add(String(c.recruiterId)); return n; });
      showNotification?.(j.message || `Demande de numéro envoyée à ${joinName(c.firstName, c.name)}.`, 'success');
      sourcing.reload?.();
    } catch (e) { showNotification?.(`Demande de numéro : ${e.message}`, 'error'); }
    finally { setNumeroEnCours(null); }
  }, [searchTaskId, showNotification, sourcing]);

  // « Demander sa dispo » (approche directe) : un InMail pour CE profil,
  // objet 'disponibilite' — le message présente l'opportunité et demande à
  // partir de quand il pourrait commencer, son préavis, et un créneau. Le
  // serveur refuse si la réponse est déjà connue (il la donne) ou la question
  // déjà posée sur ce besoin (sauf renvoi explicite) ; son message fait foi.
  // La ligne relit `disponibilite_demande` puis `disponibilite_annoncee`.
  const [dispoEnCours, setDispoEnCours] = useState(null);
  const demanderDispo = useCallback(async (c, { redemander = false } = {}) => {
    if (!searchTaskId || !c?.recruiterId || dispoEnCours) return;
    setDispoEnCours(c.recruiterId);
    try {
      const r = await fetch(`${BASE}/sourcing/${searchTaskId}/inmail`, {
        method: 'POST', headers: jsonH(),
        body: JSON.stringify({ candidates: [{ recruiterId: c.recruiterId, name: c.name, firstName: c.firstName }], objet: 'disponibilite', ...(redemander ? { redemander: true } : {}) }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 404 && !j.error) throw new Error('la demande de disponibilité arrive côté serveur');
      if (!(r.status === 202 || r.ok) || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(j.message || `Demande de disponibilité envoyée à ${joinName(c.firstName, c.name)}.`, 'success');
      sourcing.reload?.();
    } catch (e) { showNotification?.(`Demande de disponibilité : ${e.message}`, 'error'); }
    finally { setDispoEnCours(null); }
  }, [searchTaskId, dispoEnCours, showNotification, sourcing]);

  // « Demander son CV » (V2.2) : un InMail aux profils « à compléter »,
  // objet 'cv' — le message présente l'opportunité et demande le CV à la
  // boîte candidatures de l'organisation. Le serveur refuse sans boîte
  // configurée, si le CV a déjà été demandé sur ce besoin (sauf renvoi
  // explicite) ou déjà reçu ; son message fait foi. `tous` : à tous ceux du
  // groupe qui ne l'ont pas encore reçue. La ligne relit `cv_demande`.
  const [cvEnCours, setCvEnCours] = useState(null);
  const demanderCv = useCallback(async (c, { redemander = false, tous = false } = {}) => {
    if (!searchTaskId || cvEnCours) return;
    const liste = tous
      ? (Array.isArray(search?.a_completer) ? search.a_completer : []).filter(x => x && x.retire !== true && x.ne_pas_contacter !== true && !(x.cv_demande && typeof x.cv_demande === 'object' && x.cv_demande.statut !== 'erreur'))
      : (c?.recruiterId ? [c] : []);
    if (!liste.length) { showNotification?.('Aucun profil à qui demander son CV.', 'info'); return; }
    setCvEnCours(tous ? 'tous' : c.recruiterId);
    try {
      const r = await fetch(`${BASE}/sourcing/${searchTaskId}/inmail`, {
        method: 'POST', headers: jsonH(),
        body: JSON.stringify({ candidates: liste.map(x => ({ recruiterId: x.recruiterId, name: x.name, firstName: x.firstName })), objet: 'cv', ...(redemander ? { redemander: true } : {}) }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 404 && !j.error) throw new Error('la demande de CV arrive côté serveur');
      if (!(r.status === 202 || r.ok) || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(j.message || `Demande de CV envoyée à ${tous ? `${liste.length} profil${liste.length > 1 ? 's' : ''}` : joinName(c.firstName, c.name)}.`, 'success');
      sourcing.reload?.();
    } catch (e) { showNotification?.(`Demande de CV : ${e.message}`, 'error'); }
    finally { setCvEnCours(null); }
  }, [searchTaskId, search?.a_completer, cvEnCours, showNotification, sourcing]);

  const [verifOtwEnCours, setVerifOtwEnCours] = useState(false);
  // `tous` : relire aussi les profils au statut déjà connu — utile après une
  // correction de la lecture côté agent (un badge attribué à tort).
  // Classement par zone : le serveur classe les retenus d'après le lieu lu
  // sur chaque profil ; « lancer » envoie l'agent lire ce qui manque (lieu,
  // employeur, badge — même route que « Vérifier open to work », avec
  // lieu: true), puis on relit le classement à mesure que l'agent avance.
  const [classement, setClassement] = useState(null);
  const [classementDispo, setClassementDispo] = useState(true);
  const [classementEnCours, setClassementEnCours] = useState(false);
  const chargerClassement = useCallback(async () => {
    if (besoin?.id == null) { setClassement(null); return null; }
    try {
      const r = await fetch(`${BASE}/besoins/${besoin.id}/sourcing/classement${searchTaskId ? `?task_id=${searchTaskId}` : ''}`, { headers: authH() });
      if (r.status === 404 || r.status === 501 || r.status === 405) { setClassementDispo(false); return null; }
      const j = await r.json().catch(() => ({}));
      if (r.ok && j.success !== false) { setClassementDispo(true); setClassement(j.classement || null); return j.classement || null; }
    } catch { /* la carte le dit */ }
    return null;
  }, [besoin?.id, searchTaskId]);
  useEffect(() => { setClassement(null); }, [searchTaskId]);
  const classer = useCallback(async (opts = {}) => {
    if (besoin?.id == null) return;
    const cl = await chargerClassement();
    if (!opts.lancer || classementEnCours) return;
    const aLire = cl ? (cl.a_verifier?.lieu || 0) + (cl.a_verifier?.open_to_work || 0) : 1;
    if (!opts.tous && cl && aLire === 0) return;
    setClassementEnCours(true);
    try {
      const r = await fetch(`${BASE}/besoins/${besoin.id}/sourcing/open-to-work`, { method: 'POST', headers: jsonH(), body: JSON.stringify({ task_id: searchTaskId ?? undefined, lieu: true, tous: opts.tous === true || undefined }) });
      const j = await r.json().catch(() => ({}));
      if (r.status === 501 || r.status === 404 || r.status === 405) { showNotification?.(j.error || 'La lecture des profils arrive côté serveur.', 'info'); setClassementEnCours(false); return; }
      if (!r.ok || j.success === false) { showNotification?.(j.error || j.message || `HTTP ${r.status}`, 'error'); setClassementEnCours(false); return; }
      showNotification?.(j.message || 'Lecture lancée.', (j.a_verifier ?? 0) > 0 ? 'success' : 'info');
      if (!((j.a_verifier ?? 0) > 0)) { setClassementEnCours(false); return; }
      // L'agent ouvre un profil toutes les quelques secondes : on relit le
      // classement toutes les 20 s, jusqu'à ce que tout soit lu ou 6 min.
      let tours = 0;
      const tick = async () => {
        tours++;
        const c2 = await chargerClassement();
        sourcing.reload?.();
        const reste = c2 ? (c2.a_verifier?.lieu || 0) + (c2.a_verifier?.open_to_work || 0) : 1;
        if ((reste === 0 && !opts.tous) || tours >= 18) { setClassementEnCours(false); return; }
        setTimeout(tick, 20000);
      };
      setTimeout(tick, 20000);
    } catch (e) { showNotification?.(`Lecture impossible : ${e.message}`, 'error'); setClassementEnCours(false); }
  }, [besoin?.id, classementEnCours, searchTaskId, showNotification, sourcing, chargerClassement]);
  // LA GRILLE DU MANDAT : ce contre quoi les profils sont notés. Lue au
  // chargement, proposée depuis la fiche de poste (rien n'est enregistré tant
  // que le recruteur n'a pas dit oui), enregistrée, étalonnée, retirée.
  const [grille, setGrille] = useState(null);
  const [grilleDispo, setGrilleDispo] = useState(true);
  const [grilleEnCours, setGrilleEnCours] = useState(false);
  const [proposition, setProposition] = useState(null);
  const [etalonnage, setEtalonnage] = useState(null);
  const chargerGrille = useCallback(async () => {
    if (besoin?.id == null) { setGrille(null); return; }
    try {
      const r = await fetch(`${BASE}/besoins/${besoin.id}/sourcing/grille`, { headers: authH() });
      if (r.status === 404 || r.status === 501 || r.status === 405) { setGrilleDispo(false); return; }
      const j = await r.json().catch(() => ({}));
      if (r.ok && j.success !== false) { setGrilleDispo(true); setGrille(j.grille || null); }
    } catch { /* la carte le dit */ }
  }, [besoin?.id]);
  useEffect(() => { chargerGrille(); }, [chargerGrille]);
  const proposerGrille = useCallback(async () => {
    if (besoin?.id == null || grilleEnCours) return;
    setGrilleEnCours(true); setEtalonnage(null);
    try {
      const r = await fetch(`${BASE}/besoins/${besoin.id}/sourcing/grille/proposer`, { method: 'POST', headers: jsonH(), body: '{}' });
      const j = await r.json().catch(() => ({}));
      if (r.status === 404 || r.status === 501 || r.status === 405) { setGrilleDispo(false); showNotification?.(j.error || 'La grille du mandat arrive côté serveur.', 'info'); return; }
      if (!r.ok || j.success === false) { showNotification?.(j.error || `HTTP ${r.status}`, 'error'); return; }
      setProposition(j.grille || null);
      showNotification?.(j.note || 'Grille proposée : relisez-la avant de l’enregistrer.', 'info');
    } catch (e) { showNotification?.(`Proposition impossible : ${e.message}`, 'error'); }
    finally { setGrilleEnCours(false); }
  }, [besoin?.id, grilleEnCours, showNotification]);
  const enregistrerGrille = useCallback(async (g) => {
    if (besoin?.id == null || !g || grilleEnCours) return;
    setGrilleEnCours(true);
    try {
      const r = await fetch(`${BASE}/besoins/${besoin.id}/sourcing/grille`, { method: 'PUT', headers: jsonH(), body: JSON.stringify({ grille: g, source: proposition ? 'proposee' : 'manuelle' }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) { showNotification?.(j.error || `HTTP ${r.status}`, 'error'); return; }
      setProposition(null);
      showNotification?.(j.message || 'Grille enregistrée.', 'success');
      await chargerGrille();
    } catch (e) { showNotification?.(`Enregistrement impossible : ${e.message}`, 'error'); }
    finally { setGrilleEnCours(false); }
  }, [besoin?.id, grilleEnCours, proposition, showNotification, chargerGrille]);
  const retirerGrille = useCallback(async () => {
    if (besoin?.id == null || grilleEnCours) return;
    setGrilleEnCours(true);
    try {
      const r = await fetch(`${BASE}/besoins/${besoin.id}/sourcing/grille`, { method: 'DELETE', headers: jsonH() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) { showNotification?.(j.error || `HTTP ${r.status}`, 'error'); return; }
      setGrille(null); setEtalonnage(null);
      showNotification?.(j.message || 'Grille retirée.', 'success');
    } catch (e) { showNotification?.(`Retrait impossible : ${e.message}`, 'error'); }
    finally { setGrilleEnCours(false); }
  }, [besoin?.id, grilleEnCours, showNotification]);
  const etalonnerGrille = useCallback(async () => {
    if (besoin?.id == null || grilleEnCours) return;
    setGrilleEnCours(true);
    try {
      const r = await fetch(`${BASE}/besoins/${besoin.id}/sourcing/grille/etalonner`, { method: 'POST', headers: jsonH(), body: '{}' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) { showNotification?.(j.error || `HTTP ${r.status}`, 'error'); return; }
      setEtalonnage({ passes: j.passes ?? 0, echecs: j.echecs ?? 0, cas: j.cas || [] });
      showNotification?.(j.message || 'Étalonnage terminé.', (j.echecs ?? 0) > 0 ? 'error' : 'success');
    } catch (e) { showNotification?.(`Étalonnage impossible : ${e.message}`, 'error'); }
    finally { setGrilleEnCours(false); }
  }, [besoin?.id, grilleEnCours, showNotification]);
  const verifierOpenToWork = useCallback(async (opts = {}) => {
    if (besoin?.id == null || verifOtwEnCours) return;
    setVerifOtwEnCours(true);
    try {
      const r = await fetch(`${BASE}/besoins/${besoin.id}/sourcing/open-to-work`, { method: 'POST', headers: jsonH(), body: JSON.stringify({ task_id: searchTaskId ?? undefined, tous: opts?.tous === true || undefined }) });
      const j = await r.json().catch(() => ({}));
      if (r.status === 501 || r.status === 404 || r.status === 405) { showNotification?.(j.error || 'La vérification « open to work » arrive côté serveur.', 'info'); return; }
      if (!r.ok || j.success === false) { showNotification?.(j.error || j.message || `HTTP ${r.status}`, 'error'); return; }
      showNotification?.(j.message || 'Vérification lancée.', (j.a_verifier ?? 0) > 0 ? 'success' : 'info');
      setTimeout(() => sourcing.reload?.(), 20000);
    } catch (e) { showNotification?.(`Vérification impossible : ${e.message}`, 'error'); }
    finally { setVerifOtwEnCours(false); }
  }, [besoin?.id, verifOtwEnCours, searchTaskId, showNotification, sourcing]);
  // Contrôle qualité : le dernier audit de la campagne (relu à chaque
  // changement de campagne, de statut ou d'envoi), l'audit à la demande, et
  // la copie du rapport — sans nom de candidat, il peut sortir de l'ERP.
  const [audit, setAudit] = useState(null);
  const [auditDispo, setAuditDispo] = useState(true);
  const [auditEnCours, setAuditEnCours] = useState(false);
  useEffect(() => {
    if (besoin?.id == null || !searchTaskId) { setAudit(null); return undefined; }
    let vivant = true;
    (async () => {
      try {
        const r = await fetch(`${BASE}/besoins/${besoin.id}/sourcing/audit`, { headers: authH() });
        if (r.status === 404 || r.status === 501 || r.status === 405) { if (vivant) setAuditDispo(false); return; }
        const j = await r.json().catch(() => ({}));
        if (vivant && r.ok && j.success !== false) { setAuditDispo(true); setAudit(j.audit || null); }
      } catch { /* pas d'audit lisible : la carte le dit */ }
    })();
    return () => { vivant = false; };
  }, [besoin?.id, searchTaskId, search?.status, inmail?.sent]);
  const auditer = useCallback(async () => {
    if (besoin?.id == null || auditEnCours) return;
    setAuditEnCours(true);
    try {
      const r = await fetch(`${BASE}/besoins/${besoin.id}/sourcing/audit`, { method: 'POST', headers: jsonH(), body: '{}' });
      const j = await r.json().catch(() => ({}));
      if (r.status === 501 || r.status === 404 || r.status === 405) { setAuditDispo(false); showNotification?.(j.error || 'L’audit arrive côté serveur.', 'info'); return; }
      if (!r.ok || j.success === false) { showNotification?.(j.error || `HTTP ${r.status}`, 'error'); return; }
      setAudit(j.audit || null);
      const n = j.audit?.note;
      showNotification?.(`Audit terminé : ${n ?? '—'}/100, ${(j.audit?.constats || []).length} constat(s).`, Number(n) >= 80 ? 'success' : 'info');
    } catch (e) { showNotification?.(`Audit impossible : ${e.message}`, 'error'); }
    finally { setAuditEnCours(false); }
  }, [besoin?.id, auditEnCours, showNotification]);
  // « Générer le rapport » : le rapport qualité en PDF, partagé avec l'accueil.
  const { rapportEnCours, genererRapport } = useRapportQualite(showNotification);
  // Lancer UNE source sur la campagne en place — même route que le tool
  // agent sourcing_canal_lancer. Le serveur interroge, score, fusionne, puis
  // renvoie une nouvelle sélection : on recharge l'état.
  const [canalEnCours, setCanalEnCours] = useState(null);
  const lancerCanal = useCallback(async (code) => {
    if (besoin?.id == null || canalEnCours) return;
    setCanalEnCours(code);
    try {
      const r = await fetch(`${BASE}/besoins/${besoin.id}/sourcing/canaux/${encodeURIComponent(code)}`, { method: 'POST', headers: jsonH(), body: JSON.stringify({}) });
      const j = await r.json().catch(() => ({}));
      if (r.status === 501 || r.status === 404 || r.status === 405) { showNotification?.(j.error || 'Pas encore disponible côté moteur — relancez la campagne avec ce canal coché.', 'error'); return; }
      if (!r.ok || j.success === false) { showNotification?.(j.error || j.message || `HTTP ${r.status}`, 'error'); return; }
      // Le message serveur porte la raison du moteur quand rien n'est revenu
      // (« Aucun compte GitHub trouvé pour Java sur Geneva, Lausanne… ») :
      // on le montre tel quel, et pas en vert quand il n'y a rien.
      showNotification?.(j.message || 'Source lancée.', (j.ajoutes ?? 0) > 0 ? 'success' : 'info');
      sourcing.reload?.();
    } catch (e) { showNotification?.(`Lancement impossible : ${e.message}`, 'error'); }
    finally { setCanalEnCours(null); }
  }, [besoin?.id, canalEnCours, showNotification, sourcing]);
  // Relancer TOUTE la campagne, telle quelle ou avec plus de profils bruts
  // (`maxProfils`) quand le résultat ne satisfait pas : même requête, mêmes
  // réglages (ville, top N, mode, moteur, expérience, exclusions, sources,
  // jackpot, open to work), une nouvelle campagne.
  const relancer = query ? (opts = {}) => launch({ query, ville: search?.ville, topN: search?.topN || 5, mode: search?.mode, moteurV2: true, experience: search?.experience,
    jackpotEnPremier: search?.jackpot_en_premier === false ? false : undefined,
    excludeCompanies: Array.isArray(search?.exclude_companies) ? search.exclude_companies : undefined, sources: Array.isArray(search?.sources) ? search.sources : undefined,
    jackpot: Array.isArray(search?.jackpot) ? search.jackpot : undefined, openToWork: search?.open_to_work_seulement === true,
    entreprises: Array.isArray(search?.entreprises) ? search.entreprises : undefined, pays: Array.isArray(search?.pays) ? search.pays : undefined,
    maxProfils: opts && opts.maxProfils != null ? opts.maxProfils : (search?.max_profils || undefined) }) : undefined;
  const phonesN = convos.filter(c => c.status === 'phone_captured' || c.status === 'candidate_created').length;
  // Un fil existe dès l'ENVOI de l'InMail (amorcé pour relire le message) :
  // compter les fils comme des réponses annonçait « 2 candidats ont répondu »
  // sur deux InMails sans réponse (vécu 23.09). Une réponse, c'est un fil qui
  // a quitté l'attente ou qui porte un message du candidat.
  const reponsesN = convos.filter(c => (c.status && c.status !== 'waiting') || (c.messages || []).some(m => m.author === 'candidate')).length;
  const CONVO_CHIP = { waiting: { l: 'En attente de réponse', k: 'mut' }, active: { l: 'Le candidat a répondu', k: 'warn', pulse: true }, phone_captured: { l: 'Numéro récupéré', k: 'ok' }, candidate_created: { l: 'Candidat créé', k: 'ok' } };
  // Le refus lu par le moteur : la raison en mots d'écran (le serveur envoie
  // aussi son libellé), la date de recontact, la demande de ne plus être sollicité.
  const MOTIF_REFUS = { en_poste_satisfait: 'En poste, satisfait', engage_recemment: 'Engagé récemment', poste_inadapte: 'Poste inadapté', conditions: 'Conditions', pas_le_bon_moment: 'Pas le bon moment', deja_en_process: 'Déjà en discussion', quitte_le_marche: 'Quitte le marché', sans_raison: 'A décliné', autre: 'A décliné' };
  const fmtMoisAnnee = (d) => { const x = new Date(d); return Number.isNaN(x.getTime()) ? '' : x.toLocaleDateString('fr-CH', { month: 'long', year: 'numeric' }); };

  return (
    <>
      {etalonCible && (
        <ProfilEtalon cible={etalonCible} besoin={besoin} showNotification={showNotification}
          onFermer={() => setEtalonCible(null)}
          onLance={() => { setEtalonCible(null); sourcing.reload?.(); }} />
      )}
      {/* ── Pilotage : décision, compteurs, briques par source, convergence,
          brique choisie + preuve de ciblage, cadence. Tout vient de l'état
          serveur et du direct — rien n'est estimé. ── */}
      <div style={{ marginTop: '16px' }}>
        <SourceurPilotage
          besoin={besoin} search={search} inmail={inmail} entries={entries} direct={direct}
          convos={reponsesN} positionnes={positionnes}
          v2={v2Carte} candidats={candidatsCarte} cible={direct?.cible || search?.cible} gestes={gestesCarte}
          objectif={direct?.objectif || search?.objectif || null}
          onOuvrirSelection={() => defiler(refSelection)} onVoirInmails={() => defiler(refInmails)} onVoirConversations={() => defiler(refConvos)}
          onEditSearch={onEditSearch} onRelancer={relancer} onPause={basculerPause}
          onLancerCanal={lancerCanal} canalEnCours={canalEnCours} showNotification={showNotification} onRefresh={() => sourcing.reload?.()}
          onElargir={elargir} elargirEnCours={elargirEnCours}
          onVerifierOpenToWork={verifierOpenToWork} verifOtwEnCours={verifOtwEnCours}
          onDemanderNumero={demanderNumero} numeroEnCours={numeroEnCours} numeroDemandes={numeroDemandes}
          onDemanderDispo={demanderDispo} dispoEnCours={dispoEnCours}
          onDemanderCv={demanderCv} cvEnCours={cvEnCours} onOuvrirFiche={onOpenCandidat}
          onRetirer={retirerCandidat} retraitEnCours={retraitEnCours}
          onListeNoire={(c) => setListeNoireProfil(c)} listeNoireEnCours={listeNoireEnCours}
          onProposer={besoin?.id != null ? () => setProposerOuvert(true) : undefined}
          liste2={liste2} liste2Dispo={liste2Dispo} onListe2={basculerListe2} liste2EnCours={liste2EnCours}
          classement={classement} classementDispo={classementDispo} onClasser={classer} classementEnCours={classementEnCours}
          grille={grille} grilleDispo={grilleDispo} grilleEnCours={grilleEnCours} proposition={proposition} etalonnage={etalonnage}
          grilleCampagne={search?.grille ? { notee_avec_une_grille: true } : { notee_avec_une_grille: false }}
          onProposerGrille={proposerGrille} onEnregistrerGrille={enregistrerGrille} onEtalonnerGrille={etalonnerGrille}
          onRetirerGrille={retirerGrille} onAnnulerProposition={() => setProposition(null)}
          audit={audit} auditDispo={auditDispo} onAuditer={auditer} auditEnCours={auditEnCours} onRapportPdf={genererRapport} rapportEnCours={rapportEnCours} />
        <ModaleListeNoire profil={listeNoireProfil} onConfirmer={blacklister} onFermer={() => setListeNoireProfil(null)} enCours={listeNoireEnCours != null} />
        {proposerOuvert && <ProposerProfils search={search} enCours={proposerEnCours} onEnvoyer={proposer} onFermer={() => setProposerOuvert(false)} />}
      </div>

      {/* La requête exécutée et, pour une recherche par profil type, le
          raisonnement du serveur tel quel — c'est ce qui rend la dérivation
          défendable. */}
      {query && (
        <div style={{ ...CARD, marginTop: '14px', padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-3)' }}>
              {depuisProfil ? 'Recherche dérivée du profil type' : 'Requête exécutée'}
            </span>
            {aiGen && !depuisProfil && <AiBadge />}
            {depuisProfil && <Chip kind="mut" style={{ fontSize: '10.5px' }}><BI icon="person-badge" /> profil type</Chip>}
            {search.experience && <Chip kind="mut" mono style={{ fontSize: '10.5px' }}>exp. {search.experience}</Chip>}
            {Array.isArray(search.exclude_companies) && search.exclude_companies.length > 0 && (
              <Chip kind="mut" mono style={{ fontSize: '10.5px' }} title={search.exclude_companies.join(', ')}><BI icon="ban" /> sans {search.exclude_companies.join(', ')}</Chip>
            )}
            {Array.isArray(search.jackpot) && search.jackpot.length > 0 && (
              <Chip kind="warn" style={{ fontSize: '10.5px' }} title={search.jackpot_en_premier ? `Mots rares cherchés en première passe : ${search.jackpot.join(', ')}` : `Mots rares guettés : ${search.jackpot.join(', ')}`}><BI icon="key" /> jackpot : {search.jackpot.join(', ')}</Chip>
            )}
            {Array.isArray(search.entreprises) && search.entreprises.length > 0 && (
              <Chip kind="warn" style={{ fontSize: '10.5px' }} title={search.entreprises_en_premier ? `Entreprises visées, cherchées en première passe : ${search.entreprises.join(', ')}` : `Entreprises visées : ${search.entreprises.join(', ')}`}><BI icon="building" /> chez : {search.entreprises.join(', ')}</Chip>
            )}
            {Array.isArray(search.pays) && search.pays.length > 0 && (
              <Chip kind="mut" style={{ fontSize: '10.5px' }} title={`Profils cherchés en ${search.pays.join(', ')} — le poste reste à ${search.ville || 'la ville du besoin'}`}><BI icon="globe2" /> cherché en {search.pays.join(', ')}</Chip>
            )}
            {Array.isArray(search.langues) && search.langues.length > 0 && (
              <Chip kind="mut" style={{ fontSize: '10.5px' }} title={`Langues requises par le client : ${search.langues.join(', ')} — un retenu qui ne les déclare pas sur son profil est signalé sur sa ligne`}><BI icon="translate" /> {search.langues.join(', ')}</Chip>
            )}
            {search.open_to_work_seulement === true && (
              <Chip kind="ok" style={{ fontSize: '10.5px' }} title="La campagne ne retient que les profils ouverts aux opportunités : les autres sont dans les écartés, avec la raison"><BI icon="door-open" /> open to work seulement</Chip>
            )}
            {search.etalon && (
              <Chip kind="warn" style={{ fontSize: '10.5px' }} title={search.etalon.titre || 'Campagne de clones : chaque candidat est noté sur sa similarité à ce profil.'}><BI icon="people" /> clone de {search.etalon.nom || `profil #${search.etalon.id}`}</Chip>
            )}
          </div>
          <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '12px', lineHeight: 1.6, color: 'var(--text)', wordBreak: 'break-word' }}>{query}</div>
          {/* Les mots rares cherchés en premier : la requête ci-dessus est
              celle de la première passe ; la base, sans eux, prend le relais. */}
          {search?.query_base && search.query_base !== query && (
            <div style={{ marginTop: '8px', fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.5 }}>
              <BI icon="info-circle" /> Les mots rares sont cherchés en premier : c’est la requête de la première passe. Si elle ne suffit pas, la passe suivante reprend la requête complète, sans eux :
              <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11.5px', marginTop: '4px', color: 'var(--text-2)', wordBreak: 'break-word' }}>{search.query_base}</div>
            </div>
          )}
          {depuisProfil && raisonnement && (
            <div style={{ marginTop: '9px', paddingTop: '9px', borderTop: '1px solid var(--hair)', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <BI icon="info-circle" style={{ flex: 'none', marginTop: '1px', fontSize: '12px', color: 'var(--text-3)' }} />
              <span style={{ fontSize: '12px', lineHeight: 1.55, color: 'var(--text-2)' }}>{raisonnement}</span>
            </div>
          )}
        </div>
      )}

      {/* Détail d'un échec : les critères LinkedIn réellement appliqués. */}
      {search?.status === 'error' && search.filters && typeof search.filters === 'object' && !Array.isArray(search.filters) && Object.keys(search.filters).length > 0 && (
        <div style={{ marginTop: '12px', background: 'var(--danger-bg)', border: '1px solid var(--danger-bd)', borderRadius: '12px', padding: '12px 16px', fontSize: '12px', lineHeight: 1.55, color: 'var(--danger-fg)' }}>
          Critères LinkedIn appliqués : <span style={{ fontFamily: "'IBM Plex Mono',monospace" }}>{Object.entries(search.filters).map(([k, v]) => `${k}: ${String(v)}`).join(' · ')}</span> — élargissez la requête ou la zone.
        </div>
      )}

      <div ref={refSelection} />
      {/* Sélection reçue (mode manuel) — moment héros */}
      {showSelection && (
        <div style={{ ...CARD, marginTop: '16px', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '13px 18px', borderBottom: '1px solid var(--hair)', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-strong)' }}>Sélection reçue</span>
            <Chip kind="ok">{candidates.length} profils scorés</Chip>
            <span style={{ fontSize: '12px', color: 'var(--text-3)' }}>cochez les profils à contacter — score et raisons calculés par l'IA</span>
            <div style={{ flex: 1 }} />
            {search.experience && <Chip kind="mut" mono style={{ fontSize: '10.5px' }}>exp. {search.experience}</Chip>}
            {Array.isArray(search.exclude_companies) && search.exclude_companies.length > 0 && (
              <Chip kind="mut" mono style={{ fontSize: '10.5px' }} title={search.exclude_companies.join(', ')}><BI icon="ban" /> sans {search.exclude_companies.join(', ')}</Chip>
            )}
            {Array.isArray(search.jackpot) && search.jackpot.length > 0 && (
              <Chip kind="warn" style={{ fontSize: '10.5px' }} title={search.jackpot_en_premier ? `Mots rares cherchés en première passe : ${search.jackpot.join(', ')}` : `Mots rares guettés : ${search.jackpot.join(', ')}`}><BI icon="key" /> jackpot : {search.jackpot.join(', ')}</Chip>
            )}
            {Array.isArray(search.entreprises) && search.entreprises.length > 0 && (
              <Chip kind="warn" style={{ fontSize: '10.5px' }} title={search.entreprises_en_premier ? `Entreprises visées, cherchées en première passe : ${search.entreprises.join(', ')}` : `Entreprises visées : ${search.entreprises.join(', ')}`}><BI icon="building" /> chez : {search.entreprises.join(', ')}</Chip>
            )}
            {Array.isArray(search.pays) && search.pays.length > 0 && (
              <Chip kind="mut" style={{ fontSize: '10.5px' }} title={`Profils cherchés en ${search.pays.join(', ')} — le poste reste à ${search.ville || 'la ville du besoin'}`}><BI icon="globe2" /> cherché en {search.pays.join(', ')}</Chip>
            )}
            {Array.isArray(search.langues) && search.langues.length > 0 && (
              <Chip kind="mut" style={{ fontSize: '10.5px' }} title={`Langues requises par le client : ${search.langues.join(', ')} — un retenu qui ne les déclare pas sur son profil est signalé sur sa ligne`}><BI icon="translate" /> {search.langues.join(', ')}</Chip>
            )}
            {search.open_to_work_seulement === true && (
              <Chip kind="ok" style={{ fontSize: '10.5px' }} title="La campagne ne retient que les profils ouverts aux opportunités : les autres sont dans les écartés, avec la raison"><BI icon="door-open" /> open to work seulement</Chip>
            )}
            {search.etalon && (
              <Chip kind="warn" style={{ fontSize: '10.5px' }} title={search.etalon.titre || 'Campagne de clones : chaque candidat est noté sur sa similarité à ce profil.'}><BI icon="people" /> clone de {search.etalon.nom || `profil #${search.etalon.id}`}</Chip>
            )}
            {aiGen && !depuisProfil && <AiBadge label="query générée par l'IA" />}
            {depuisProfil && <Chip kind="mut" style={{ fontSize: '10.5px' }} title={raisonnement || undefined}><BI icon="person-badge" /> profils similaires</Chip>}
          </div>
          {search.commentaire && <div style={{ padding: '10px 18px 0', fontSize: '12.5px', color: 'var(--text-2)', fontStyle: 'italic' }}>{search.commentaire}</div>}
          <div style={{ padding: '16px 18px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(310px,1fr))', gap: '12px' }}>
            {candidates.map((c, i) => {
              const retire = c.retire === true;
              const on = !retire && checked.has(c.recruiterId);
              const score = c.score != null ? Math.round(Number(c.score)) : null;
              const name = joinName(c.firstName, c.name);
              const role = c.role || c.headline || c.titre || '';
              const raisons = Array.isArray(c.raisons) ? c.raisons : (c.raisons ? [c.raisons] : []);
              return (
                <div key={c.recruiterId ?? i} onClick={() => { if (!retire) toggle(c.recruiterId); }}
                  title={retire ? `Retiré de la campagne${c.retire_motif ? ` — ${c.retire_motif}` : ''} : ne peut pas être coché, aucun InMail ne partira` : undefined}
                  style={{ border: on ? '1px solid var(--text-strong)' : '1px solid var(--border)', background: on ? 'var(--ghost-bg-hover)' : 'var(--ghost-bg)', borderRadius: '11px', padding: '13px 14px', cursor: retire ? 'default' : 'pointer', opacity: retire ? .6 : 1, transition: 'border-color .12s ease, background .12s ease' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ width: '19px', height: '19px', borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', border: on ? 'none' : '1.5px solid var(--input-border)', background: on ? 'var(--accent)' : 'transparent', color: 'var(--accent-fg)' }}>
                      {on && <BI icon="check-lg" style={{ fontSize: '12px' }} />}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
                      {role && <div style={{ marginTop: '1px', fontSize: '11.5px', color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{role}</div>}
                    </div>
                    {retire && <Chip kind="mut" style={{ fontSize: '10.5px' }}><BI icon="x-circle-fill" /> Retiré</Chip>}
                    {c.liste2 === true && <Chip kind="mut" style={{ fontSize: '10.5px' }} title="Dans la liste 2 : les profils mis de côté sur cette campagne"><BI icon="bookmark-fill" /> Liste 2</Chip>}
                    {score != null && <Chip kind={scoreKind(score)} mono style={{ fontSize: '12.5px', padding: '4px 9px' }}>{score}</Chip>}
                  </div>
                  {raisons.length > 0 && (
                    <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                      {raisons.map((rz, k) => (
                        <div key={k} style={{ display: 'flex', alignItems: 'flex-start', gap: '7px', fontSize: '12px', lineHeight: 1.45, color: 'var(--text-2)' }}>
                          <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: 'var(--text-faint)', flex: 'none', marginTop: '6px' }} />
                          <span style={{ flex: 1 }}>{rz}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <ContactKnown c={c} />
                  {c.cv && <div style={{ marginTop: '8px' }}><CvRecu c={c} /></div>}
                  {c.clone === true && <div style={{ marginTop: '8px' }}><BadgeClone candidat={c} nom={search?.etalon?.nom} /></div>}
                  {Array.isArray(c.jackpot) && c.jackpot.length > 0 && <div style={{ marginTop: '8px' }}><BadgeJackpot candidat={c} /></div>}
                  {Array.isArray(c.manque) && c.manque.length > 0 && <div style={{ marginTop: '8px' }}><BadgeManque candidat={c} /></div>}
                  {c.ne_pas_contacter === true && <div style={{ marginTop: '8px' }}><BadgeNePasContacter candidat={c} /></div>}
                  {'open_to_work' in c && <div style={{ marginTop: '8px' }}><BadgeOpenToWork candidat={c} /></div>}
                  {c.elargi === true && <div style={{ marginTop: '8px' }}><BadgeElargi candidat={c} passes={v2Carte?.passes} /></div>}
                  <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <LienProfilSource c={c} />
                    <span style={{ flex: 1 }} />
                    <BesoinBtn c={c} />
                    <VivierBtn c={c} />
                    <CloneBtn c={c} />
                  </div>
                </div>
              );
            })}
          </div>
          {/* Profils sous le seuil — repliés, repêchables au vivier */}
          {Array.isArray(search.rejected_below_threshold) && search.rejected_below_threshold.length > 0 && (
            <div style={{ borderTop: '1px solid var(--hair)' }}>
              <button onClick={() => setShowRejected(v => !v)} className="src-row-hover"
                style={{ display: 'flex', alignItems: 'center', gap: '9px', width: '100%', padding: '11px 18px', border: 'none', background: 'transparent', color: 'var(--text-2)', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}>
                <BI icon={showRejected ? 'chevron-up' : 'chevron-down'} style={{ fontSize: '11px', color: 'var(--text-faint)' }} />
                {search.rejected_below_threshold.length} profil{search.rejected_below_threshold.length > 1 ? 's' : ''} sous le seuil
                <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>— non retenus par l'IA, repêchables au vivier</span>
              </button>
              {showRejected && (
                <div style={{ padding: '0 18px 16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(310px,1fr))', gap: '12px' }}>
                  {search.rejected_below_threshold.map((c, i) => {
                    const score = c.score != null ? Math.round(Number(c.score)) : null;
                    const name = joinName(c.firstName, c.name);
                    const role = c.role || c.headline || c.titre || '';
                    return (
                      <div key={c.recruiterId ?? i} style={{ border: '1px solid var(--border)', background: 'var(--ghost-bg)', borderRadius: '11px', padding: '13px 14px', opacity: 0.8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
                            {role && <div style={{ marginTop: '1px', fontSize: '11.5px', color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{role}</div>}
                          </div>
                          {score != null && <Chip kind={scoreKind(score)} mono style={{ fontSize: '12.5px', padding: '4px 9px' }}>{score}</Chip>}
                        </div>
                        {c.raison && <div style={{ marginTop: '8px', fontSize: '12px', lineHeight: 1.45, color: 'var(--text-2)', fontStyle: 'italic' }}>{c.raison}</div>}
                        <ContactKnown c={c} />
                        {c.cv && <div style={{ marginTop: '8px' }}><CvRecu c={c} /></div>}
                        {c.clone === true && <div style={{ marginTop: '8px' }}><BadgeClone candidat={c} nom={search?.etalon?.nom} /></div>}
                  {Array.isArray(c.jackpot) && c.jackpot.length > 0 && <div style={{ marginTop: '8px' }}><BadgeJackpot candidat={c} /></div>}
                  {Array.isArray(c.manque) && c.manque.length > 0 && <div style={{ marginTop: '8px' }}><BadgeManque candidat={c} /></div>}
                  {c.ne_pas_contacter === true && <div style={{ marginTop: '8px' }}><BadgeNePasContacter candidat={c} /></div>}
                  {'open_to_work' in c && <div style={{ marginTop: '8px' }}><BadgeOpenToWork candidat={c} /></div>}
                  {c.elargi === true && <div style={{ marginTop: '8px' }}><BadgeElargi candidat={c} passes={v2Carte?.passes} /></div>}
                        <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <LienProfilSource c={c} />
                          <span style={{ flex: 1 }} />
                          <BesoinBtn c={c} />
                          <VivierBtn c={c} />
                          <CloneBtn c={c} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '13px 18px', borderTop: '1px solid var(--hair)', background: 'var(--panel)', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>{selected.length} sélectionnés sur {candidates.length}</span>
            <span style={{ fontSize: '11.5px', color: 'var(--text-3)' }}>chaque InMail est personnalisé avec le contexte du besoin</span>
            <div style={{ flex: 1 }} />
            <button onClick={() => sendInmails(searchTaskId, selected)} disabled={sending || selected.length === 0 || !searchTaskId}
              title={!searchTaskId ? 'Identifiant de tâche indisponible — relancez une recherche pour pouvoir envoyer.' : undefined}
              style={{ ...PRIMARY_BTN, cursor: (sending || !selected.length || !searchTaskId) ? 'not-allowed' : 'pointer', opacity: (sending || !selected.length || !searchTaskId) ? 0.45 : 1 }}>
              <BI icon={sending ? 'arrow-repeat' : 'send'} style={sending ? { animation: 'ats-spin .7s linear infinite' } : undefined} /> {sending ? 'Envoi…' : `Envoyer les InMails (${selected.length})`}
            </button>
          </div>
        </div>
      )}

      <div ref={refInmails} />
      {/* Suivi des InMails */}
      {showTracking && (
        <div style={{ ...CARD, marginTop: '16px', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '13px 18px', borderBottom: '1px solid var(--hair)', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-strong)' }}>Suivi des InMails</span>
            <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '12px', fontWeight: 600, color: 'var(--text-2)', background: 'var(--code-bg)', padding: '3px 9px', borderRadius: '6px' }}>{sentN}/{totalN}</span>
            {autoMode && <Chip kind="warn" style={{ fontSize: '11px' }}>Envoi automatique — Top {totalN || 'N'} contactés dès la fin de la recherche</Chip>}
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: '11.5px', color: 'var(--text-3)' }}>actualisé toutes les 15 s</span>
          </div>
          {sendRows.length === 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 18px' }}>
              <span style={{ width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', background: 'var(--code-bg)', color: 'var(--text-faint)' }}>
                {awaitingReport ? <span className="src-dot" /> : <BI icon="check-lg" style={{ fontSize: '11px' }} />}
              </span>
              <span style={{ fontSize: '12.5px', color: 'var(--text-2)' }}>
                {awaitingReport ? 'Envoi en cours — le rapport détaillé arrive (actualisation automatique).' : `${sentN}/${totalN} InMails envoyés — détail indisponible pour cette campagne.`}
              </span>
            </div>
          )}
          {sendRows.map((s, i) => {
            const col = s.st === 'ok' ? undefined : s.st === 'fail' ? 'var(--danger-fg)' : 'var(--text-faint)';
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 18px', borderBottom: '1px solid var(--hair)', flexWrap: 'wrap' }}>
                <span className={s.st === 'ok' ? 'src-ok-fg' : undefined} style={{ width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', background: 'var(--code-bg)', color: col }}>
                  {s.st === 'ok' ? <BI icon="check-lg" style={{ fontSize: '11px' }} /> : s.st === 'fail' ? <BI icon="x-lg" style={{ fontSize: '10px' }} /> : <span className="src-dot" />}
                </span>
                <span style={{ flex: 1, minWidth: '160px', fontSize: '13.5px', fontWeight: 600, color: 'var(--text-strong)' }}>{s.name}</span>
                {s.err && <span style={{ fontSize: '11.5px', color: 'var(--danger-fg)' }}>{s.err}</span>}
                <Chip kind={s.st === 'ok' ? 'ok' : s.st === 'fail' ? 'bad' : 'mut'}>{s.st === 'ok' ? 'Envoyé' : s.st === 'fail' ? 'Échec' : 'En file'}</Chip>
              </div>
            );
          })}
        </div>
      )}

      <div ref={refConvos} />
      {/* Conversations (Erpia Agent) — visible dès qu'un fil existe, ou en
          attente dès qu'au moins un InMail est parti (route présente). */}
      {(convos.length > 0 || convosAvant.length > 0 || (Array.isArray(conv.convos) && sentN > 0)) && (
        <div style={{ ...CARD, marginTop: '16px', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '13px 18px', borderBottom: '1px solid var(--hair)', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-strong)' }}>Conversations</span>
            <Chip kind="warn" style={{ fontSize: '11px' }}><BI icon="chat-square-text" /> Erpia Agent répond automatiquement</Chip>
            <span style={{ fontSize: '12px', color: 'var(--text-3)' }}>objectif : obtenir un échange téléphonique — le numéro récupéré crée le candidat</span>
            <div style={{ flex: 1 }} />
            <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '12px', fontWeight: 600, color: 'var(--text-2)', background: 'var(--code-bg)', padding: '3px 9px', borderRadius: '6px' }}>{reponsesN} réponse{reponsesN > 1 ? 's' : ''} sur {convos.length} contacté{convos.length > 1 ? 's' : ''} · {phonesN} numéro{phonesN > 1 ? 's' : ''}</span>
          </div>
          {convos.length === 0 && sentN > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 18px' }}>
              <span style={{ width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', background: 'var(--code-bg)', color: 'var(--text-faint)' }}>
                <span className="src-dot" />
              </span>
              <span style={{ flex: 1, minWidth: '200px', fontSize: '12.5px', color: 'var(--text-2)' }}>En attente de réponse des candidats contactés — les fils de discussion apparaîtront ici, et Erpia Agent y répondra automatiquement.</span>
              <Chip kind="mut" pulse>En attente de réponse</Chip>
            </div>
          )}
          {lignesConvos.map((cv, i) => {
            const id = cv.id ?? cv.profileId ?? i;
            const meta = CONVO_CHIP[cv.status] || CONVO_CHIP.waiting;
            const msgs = Array.isArray(cv.messages) ? cv.messages : [];
            const lastMsg = msgs[msgs.length - 1];
            const name = joinName(cv.first_name, cv.name);
            const initials = name.split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();
            const lastLine = (cv.status === 'waiting' && cv.nextFollowupAt)
              ? `Relance prévue le ${fmtDate(cv.nextFollowupAt)}`
              : (lastMsg ? `${lastMsg.author === 'agent' ? 'Erpia Agent' : name} — ${lastMsg.text}` : '');
            const expanded = openConvo === id;
            const hasPhone = (cv.status === 'phone_captured' || cv.status === 'candidate_created') && cv.phone;
            // Numéro AU DOSSIER (phone_source: 'profil') : affiché même si le fil
            // reste « En attente de réponse » — c'est normal, il ne vient pas de
            // la conversation. phone_captured = numéro obtenu par la discussion.
            const dossierPhone = !hasPhone && cv.phone && cv.phone_source === 'profil';
            // « Créer la fiche » proposé dès que le candidat a répondu (active),
            // et bien sûr quand le numéro est récupéré. already:true côté back
            // rend le re-clic inoffensif.
            const canCreate = cv.status === 'phone_captured' || cv.status === 'active';
            return (
              <div key={id} style={{ borderBottom: '1px solid var(--hair)' }}>
                {i === convos.length && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '9px 18px', background: 'var(--code-bg)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)' }}>
                    <BI icon="clock-history" /> Campagnes précédentes sur ce besoin{search?.created_at ? ` — avant la recherche du ${fmtDate(search.created_at)}` : ''}
                  </div>
                )}
                <div onClick={() => setOpenConvo(expanded ? null : id)} className="src-row-hover"
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 18px', cursor: 'pointer', flexWrap: 'wrap' }}>
                  <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: 'var(--accent-soft-bg)', color: 'var(--accent-soft-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'IBM Plex Mono',monospace", fontSize: '11.5px', fontWeight: 600, flex: 'none' }}>{initials}</div>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--text-strong)' }}>{name}</div>
                    <div style={{ marginTop: '1px', fontSize: '12px', color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{lastLine}</div>
                  </div>
                  {cv.contact_known && <Chip kind="ok" style={{ fontSize: '10.5px' }} title="Candidat connu — numéro au dossier"><BI icon="person-vcard" /> Connu</Chip>}
                  {(hasPhone || dossierPhone) && (
                    <span title={dossierPhone ? 'Numéro au dossier (profil connu) — pas issu de la conversation' : 'Numéro obtenu par la conversation'}
                      style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '12px', fontWeight: 600, color: 'var(--text-strong)', flex: 'none' }}>
                      <BI icon="telephone-fill" style={{ fontSize: '10.5px', marginRight: '4px' }} />{cv.phone}{dossierPhone && <span style={{ fontWeight: 400, color: 'var(--text-faint)' }}> · dossier</span>}
                    </span>
                  )}
                  {cv.disponibilite_libelle && <Chip kind="ok" style={{ fontSize: '10.5px' }} title="Dit par le candidat dans sa réponse : quand il pourrait commencer, et son préavis — écrit sur sa fiche si sa case était vide"><BI icon="calendar-check" /> {cv.disponibilite_libelle}</Chip>}
                  {cv.motif_refus
                    ? <Chip kind="mut" style={{ fontSize: '10.5px' }} title={`A décliné — ${cv.motif_refus_libelle || cv.motif_refus}. Raison lue par le moteur dans sa réponse.`}><BI icon="hand-thumbs-down" /> {MOTIF_REFUS[cv.motif_refus] || cv.motif_refus_libelle || 'A décliné'}</Chip>
                    : <Chip kind={meta.k} pulse={meta.pulse}>{meta.l}</Chip>}
                  {cv.retire && <Chip kind="mut" style={{ fontSize: '10.5px' }} title={`Retiré de la campagne${cv.retire_motif ? ` — ${cv.retire_motif}` : ''} : aucune relance ni réponse automatique`}><BI icon="x-circle-fill" /> Retiré de la campagne</Chip>}
                  {cv.ne_plus_contacter
                    ? <Chip kind="bad" style={{ fontSize: '10.5px' }} title="Le candidat a demandé à ne plus être sollicité : sa fiche est marquée « ne pas contacter », aucun InMail ne repartira"><BI icon="slash-circle" /> Ne plus contacter</Chip>
                    : (cv.recontact_le ? <Chip kind="warn" style={{ fontSize: '10.5px' }} title="Le candidat a laissé la porte ouverte : le moteur lui a proposé de reprendre contact à cette date"><BI icon="calendar-event" /> À recontacter en {fmtMoisAnnee(cv.recontact_le)}</Chip> : null)}
                  {(cv.updated_at || lastMsg?.sentAt) && <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px', color: 'var(--text-faint)', flex: 'none' }}>{fmtDate(cv.updated_at || lastMsg?.sentAt)}</span>}
                  <BI icon={expanded ? 'chevron-up' : 'chevron-down'} style={{ color: 'var(--text-faint)', fontSize: '12px', flex: 'none' }} />
                </div>
                {expanded && (
                  <div style={{ padding: '2px 18px 14px 64px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {/* Style messagerie : agent à GAUCHE, candidat à DROITE */}
                    {msgs.map((m, k) => {
                      const agent = m.author === 'agent';
                      return (
                        <div key={k} style={{ display: 'flex', justifyContent: agent ? 'flex-start' : 'flex-end' }}>
                          <div style={{ maxWidth: '78%', padding: '9px 12px', borderRadius: agent ? '10px 10px 10px 3px' : '10px 10px 3px 10px', background: agent ? 'var(--code-bg)' : 'transparent', border: agent ? '1px solid transparent' : '1px solid var(--border)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '4px' }}>
                              <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '10px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: agent ? 'var(--text-2)' : 'var(--text-3)' }}>{agent ? (k === 0 ? 'InMail · Erpia Agent' : 'Erpia Agent') : name}</span>
                              {m.sentAt && <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '10px', color: 'var(--text-faint)' }}>{fmtDate(m.sentAt)}</span>}
                            </div>
                            <div style={{ fontSize: '12.5px', lineHeight: 1.55, color: 'var(--text)' }}>{m.text}</div>
                          </div>
                        </div>
                      );
                    })}
                    {cv.status === 'waiting' && dossierPhone && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '10px 13px', borderRadius: '9px', background: 'var(--code-bg)', flexWrap: 'wrap' }}>
                        <BI icon="person-vcard" style={{ flex: 'none', color: 'var(--text-2)', fontSize: '13px' }} />
                        <span style={{ flex: 1, minWidth: '200px', fontSize: '12.5px', color: 'var(--text-2)' }}>
                          Candidat connu — numéro au dossier : <b style={{ fontFamily: "'IBM Plex Mono',monospace", color: 'var(--text-strong)' }}>{cv.phone}</b>{cv.email && <> · <b style={{ fontFamily: "'IBM Plex Mono',monospace", color: 'var(--text-strong)' }}>{cv.email}</b></>} — le fil reste en attente de réponse InMail.
                        </span>
                      </div>
                    )}
                    {canCreate && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '10px 13px', borderRadius: '9px', background: 'var(--code-bg)', flexWrap: 'wrap' }}>
                        <BI icon={hasPhone ? 'telephone' : dossierPhone ? 'person-vcard' : 'person-plus'} style={{ flex: 'none', color: 'var(--text-2)', fontSize: '13px' }} />
                        <span style={{ flex: 1, minWidth: '200px', fontSize: '12.5px', color: 'var(--text-2)' }}>
                          {hasPhone
                            ? <>Numéro récupéré par l'agent — <b style={{ fontFamily: "'IBM Plex Mono',monospace", color: 'var(--text-strong)' }}>{cv.phone}</b>{cv.availability && <> · à appeler : <b style={{ color: 'var(--text-strong)' }}>{cv.availability}</b></>}{cv.email && <> · <b style={{ fontFamily: "'IBM Plex Mono',monospace", color: 'var(--text-strong)' }}>{cv.email}</b></>}</>
                            : dossierPhone
                              ? <>Candidat connu — numéro au dossier : <b style={{ fontFamily: "'IBM Plex Mono',monospace", color: 'var(--text-strong)' }}>{cv.phone}</b>{cv.email && <> · <b style={{ fontFamily: "'IBM Plex Mono',monospace", color: 'var(--text-strong)' }}>{cv.email}</b></>}</>
                              : 'Le candidat a répondu — créez la fiche sans attendre le numéro.'}
                        </span>
                        <button onClick={(e) => { e.stopPropagation(); conv.createCandidate(cv); }} disabled={conv.creatingId != null}
                          style={{ ...PRIMARY_BTN, padding: '7px 12px', fontSize: '12px', opacity: conv.creatingId != null ? 0.6 : 1 }}>
                          <BI icon={conv.creatingId === id ? 'arrow-repeat' : 'plus-lg'} style={conv.creatingId === id ? { animation: 'ats-spin .7s linear infinite' } : undefined} />Créer la fiche
                        </button>
                      </div>
                    )}
                    {cv.status === 'candidate_created' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '10px 13px', borderRadius: '9px', background: 'var(--code-bg)', flexWrap: 'wrap' }}>
                        <BI icon="person-check" style={{ flex: 'none', color: 'var(--text-2)', fontSize: '13px' }} />
                        <span style={{ flex: 1, minWidth: '200px', fontSize: '12.5px', color: 'var(--text-2)' }}>Fiche candidat créée{cv.phone && <> — <b style={{ fontFamily: "'IBM Plex Mono',monospace", color: 'var(--text-strong)' }}>{cv.phone}</b></>}{cv.availability && <> · à appeler : <b style={{ color: 'var(--text-strong)' }}>{cv.availability}</b></>}</span>
                        {cv.candidate_id != null && onOpenCandidat && (
                          <button onClick={(e) => { e.stopPropagation(); onOpenCandidat(cv.candidate_id); }} className="src-row-hover" style={{ ...GHOST_BTN, padding: '7px 12px', fontSize: '12px' }}>
                            <BI icon="box-arrow-up-right" style={{ fontSize: '11px' }} />Ouvrir la fiche
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {convosAvant.length > 0 && (
            <div style={{ borderTop: '1px solid var(--hair)' }}>
              <button onClick={() => setVoirAvant(v => !v)} className="src-row-hover"
                style={{ display: 'flex', alignItems: 'center', gap: '9px', width: '100%', padding: '11px 18px', border: 'none', background: 'transparent', color: 'var(--text-2)', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}>
                <BI icon={voirAvant ? 'chevron-up' : 'chevron-down'} style={{ fontSize: '11px', color: 'var(--text-faint)' }} />
                {convosAvant.length} conversation{convosAvant.length > 1 ? 's' : ''} de campagnes précédentes sur ce besoin
                <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>— {voirAvant ? 'masquer' : 'afficher'}</span>
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ── Fusion des tâches GET /sourcing → une ligne par besoin chassé ────────────
function mergeCampaigns(tasks) {
  const byBesoin = new Map();
  for (const t of tasks) {
    const key = t.besoin_id ?? t.besoinId ?? `?${t.task_id}`;
    if (!byBesoin.has(key)) byBesoin.set(key, { besoin_id: t.besoin_id ?? t.besoinId, titre: t.besoin_titre, client: t.besoin_client || null, search: null, inmail: null, when: null });
    const g = byBesoin.get(key);
    if (t.besoin_titre && !g.titre) g.titre = t.besoin_titre;
    const ts = t.updated_at || t.created_at || t.date;
    if (ts && (!g.when || ts > g.when)) g.when = ts;
    if ((t.kind || 'search') === 'search') { if (!g.search || (t.task_id ?? 0) > (g.search.task_id ?? 0)) g.search = t; }
    else { if (!g.inmail || (t.task_id ?? 0) > (g.inmail.task_id ?? 0)) g.inmail = t; }
  }
  const rows = [...byBesoin.values()].map(g => {
    const s = g.search, m = g.inmail;
    const mode = s?.mode || m?.mode;
    let chip = { l: 'Brouillon', k: 'mut' }, pulse = false, counter = null, selLabel = null;
    if (s?.status === 'pending') { chip = { l: 'Recherche en cours', k: 'warn' }; pulse = true; }
    else if (s?.status === 'error') chip = { l: 'Erreur', k: 'bad' };
    else if (m) { chip = { l: mode === 'auto' ? 'Envoi auto' : 'InMails', k: 'mut' }; counter = `${m.sent ?? 0}/${m.total ?? (m.sent ?? 0)}`; }
    else if (s?.status === 'selection_received') {
      chip = { l: 'Sélection prête', k: 'ok' };
      if (s.candidats_selectionnes != null) selLabel = `${s.candidats_selectionnes} sélectionnés`;
    }
    const excl = Array.isArray(s?.exclude_companies) ? s.exclude_companies : [];
    return { besoin_id: g.besoin_id, titre: g.titre, client: g.client, ville: s?.ville || null, engine: s?.engine || null, en_pause: !!s?.en_pause, deleted: !g.titre, query: s?.query || '', experience: s?.experience, exclusions: excl, chip, pulse, counter, selLabel, when: g.when, error: s?.error };
  });
  rows.sort((a, b) => (b.when || '').localeCompare(a.when || ''));
  return rows;
}

// ── Écran « Sourceur » (menu) : campagnes LinkedIn de l'organisation ─────────
// Vue liste (/sourceur) + vue campagne (/sourceur/:besoinId — le Précédent du
// navigateur ramène sur la campagne d'avant). GET /sourcing absent (404) →
// liste masquée, l'écran reste utilisable (picker + campagne par besoin).
// « Générer le rapport » : le rapport qualité du sourcing en PDF — vue
// d'ensemble des 30 derniers jours et le dernier audit de chaque campagne,
// sans nom de candidat — téléchargé pour être transmis à la main. Partagé
// par l'accueil « Vos campagnes » (toujours visible) et la carte Contrôle
// qualité d'une campagne.
export function useRapportQualite(showNotification) {
  const [rapportEnCours, setRapportEnCours] = useState(false);
  const genererRapport = useCallback(async () => {
    if (rapportEnCours) return;
    setRapportEnCours(true);
    try {
      const r = await fetch(`${BASE}/sourcing/audit/rapport.pdf?jours=30`, { headers: authH() });
      if (r.status === 404 || r.status === 501 || r.status === 405) { showNotification?.('Le rapport arrive côté serveur.', 'info'); return; }
      if (!r.ok) { const j = await r.json().catch(() => ({})); showNotification?.(j.error || `HTTP ${r.status}`, 'error'); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `rapport-qualite-sourcing-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      showNotification?.('Rapport généré — le PDF est dans vos téléchargements.', 'success');
    } catch (e) { showNotification?.(`Rapport impossible : ${e.message}`, 'error'); }
    finally { setRapportEnCours(false); }
  }, [rapportEnCours, showNotification]);
  return { rapportEnCours, genererRapport };
}

export function SourceurPage({ showNotification, onOpenBesoin, onOpenCandidat, routeBesoinId, onSelectBesoin, onClearBesoin }) {
  // Essai du nouveau style : même bascule partagée que Candidats et Besoins.
  const { actif: skinV2, basculer: basculerSkin } = useSkinV2();
  const [besoins, setBesoins] = useState([]);
  const [loadingBesoins, setLoadingBesoins] = useState(true);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Deux entrées pour une campagne : par besoin (classique) ou par CANDIDAT
  // (« trouve-moi des profils comme lui »). Le pipeline reste rattaché à un
  // besoin — le candidat fournit le profil de référence.
  const [entree, setEntree] = useState('besoin'); // 'besoin' | 'candidat'
  const [profilRef, setProfilRef] = useState(null); // { id, nom, texte }
  const [cands, setCands] = useState(null);
  const [candQ, setCandQ] = useState('');
  const [creating, setCreating] = useState(false); // « Nouvelle campagne » sans besoin encore choisi
  const rapportQualite = useRapportQualite(showNotification);
  const [live, setLive] = useState(false); // vue « Suivi en direct » (GET /sourcing/dashboard)
  const [campaigns, setCampaigns] = useState(undefined); // undefined = pas encore su, null = route absente
  const [loadingCamp, setLoadingCamp] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { const r = await APIService.getBesoins(); if (!cancelled) setBesoins(r.besoins || []); }
      catch { if (!cancelled) setBesoins([]); }
      finally { if (!cancelled) setLoadingBesoins(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadCampaigns = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoadingCamp(true);
    try {
      const r = await fetch(`${BASE}/sourcing`, { headers: authH() });
      if (r.status === 404) { setCampaigns(null); return; }
      if (r.ok) {
        const j = await r.json().catch(() => ({}));
        const arr = j.data || j.campaigns || j.tasks || (Array.isArray(j) ? j : []);
        setCampaigns(Array.isArray(arr) ? arr : []);
      }
    } catch { /* offline → on garde l'état courant */ }
    finally { if (!silent) setLoadingCamp(false); }
  }, []);
  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  // Rafraîchissement AUTO de la liste : 30 s, onglet visible et vue liste
  // affichée seulement, silencieux (pas de spinner), une requête à la fois —
  // les campagnes en cours avancent sans cliquer Rafraîchir.
  const listPollBusy = useRef(false);
  const onList = !creating && routeBesoinId == null;
  const routeAbsente = campaigns === null;
  useEffect(() => {
    if (!onList || routeAbsente) return undefined;
    const iv = setInterval(async () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (listPollBusy.current) return;
      listPollBusy.current = true;
      try { await loadCampaigns({ silent: true }); } finally { listPollBusy.current = false; }
    }, 30000);
    return () => clearInterval(iv);
  }, [onList, routeAbsente, loadCampaigns]);

  // Résout routeBesoinId → objet besoin (liste déjà chargée, sinon fetch direct).
  useEffect(() => {
    if (routeBesoinId == null) { setSelected(null); return undefined; }
    setCreating(false); setPickerOpen(false);
    if (selected && String(selected.id) === String(routeBesoinId)) return undefined;
    const inList = besoins.find(b => String(b.id) === String(routeBesoinId));
    if (inList) { setSelected(inList); return undefined; }
    let cancelled = false;
    APIService.getBesoin(routeBesoinId)
      .then(b => { if (!cancelled && b) setSelected(b); })
      .catch(() => { /* besoin introuvable → l'écran reste sur le sélecteur */ });
    return () => { cancelled = true; };
  }, [routeBesoinId, besoins, selected]);

  const pick = (b) => { setPickerOpen(false); setCreating(false); onSelectBesoin?.(b.id); };

  // Liste de candidats pour l'entrée « profil type ».
  useEffect(() => {
    if (entree !== 'candidat' || !(creating || pickerOpen || !selected)) return;
    let annule = false;
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ page: '1', limit: '8' });
        if (candQ.trim()) params.set('search', candQ.trim());
        const r = await fetch(`${BASE}/candidats/vue?${params.toString()}`, { headers: authH() });
        const j = await r.json().catch(() => ({}));
        if (!annule) setCands(Array.isArray(j.data) ? j.data : []);
      } catch { if (!annule) setCands([]); }
    }, 250);
    return () => { annule = true; clearTimeout(t); };
  }, [entree, candQ, creating, pickerOpen, selected]);

  const q = filter.trim().toLowerCase();
  const matches = q
    ? besoins.filter(b => `${b.titre || ''} ${b.client || ''}`.toLowerCase().includes(q))
    : besoins;

  const rows = useMemo(() => (Array.isArray(campaigns) ? mergeCampaigns(campaigns) : []), [campaigns]);

  // Filtre de période (dernière activité) + pagination pilule (10 / page).
  const [period, setPeriod] = useState('all'); // 'all' | jours ('30' | '90' | '180')
  const [page, setPage] = useState(1);
  const PAGE_LEN = 10;
  const filteredRows = useMemo(() => {
    if (period === 'all') return rows;
    const cutoff = Date.now() - Number(period) * 86400000;
    return rows.filter(r => r.when && new Date(r.when).getTime() >= cutoff);
  }, [rows, period]);
  const pages = Math.max(1, Math.ceil(filteredRows.length / PAGE_LEN));
  useEffect(() => { if (page > pages) setPage(pages); }, [pages, page]);
  const pageRows = filteredRows.slice((page - 1) * PAGE_LEN, page * PAGE_LEN);
  const pickPeriod = (p) => { setPeriod(p); setPage(1); };

  const campView = creating || routeBesoinId != null;

  return (
    <div data-skin={skinV2 ? 'v2' : undefined} style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      {!campView ? (
        <>
          {/* ── Vue liste ── (en v2 l'accueil « Vos campagnes » porte son
              propre en-tête ; l'en-tête historique ne sert plus qu'au suivi
              en direct et à l'ancien style) */}
          {(!skinV2 || live) && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '260px' }}>
              <h1 style={{ margin: 0, fontFamily: "'Archivo',sans-serif", fontSize: '26px', fontWeight: 800, letterSpacing: '-0.025em', color: 'var(--text-strong)' }}>Sourceur</h1>
              <div style={{ marginTop: '5px', fontSize: '13px', color: 'var(--text-2)' }}>Recherche IA de profils LinkedIn, sélection et envoi/suivi des InMails — toutes les campagnes de l'organisation.</div>
            </div>
            <BoutonEssaiV2 actif={skinV2} onToggle={basculerSkin} />
            {/* Suivi en direct : l'état de toutes les campagnes, rafraîchi seul. */}
            <button onClick={() => setLive(v => !v)} className="src-row-hover"
              style={{ ...GHOST_BTN, padding: '9px 13px', fontSize: '13px', ...(live ? { borderColor: 'var(--accent)', color: 'var(--accent)' } : {}) }}>
              <BI icon="broadcast" style={{ fontSize: '12px' }} />{live ? 'Retour à la liste' : 'Suivi en direct'}
            </button>
            {campaigns !== null && !live && (
              <button onClick={loadCampaigns} disabled={loadingCamp} className="src-row-hover" style={{ ...GHOST_BTN, padding: '9px 13px', fontSize: '13px' }}>
                <BI icon="arrow-repeat" style={loadingCamp ? { animation: 'ats-spin .7s linear infinite' } : undefined} />Rafraîchir
              </button>
            )}
            <button onClick={rapportQualite.genererRapport} disabled={rapportQualite.rapportEnCours} className="src-row-hover" style={{ ...GHOST_BTN, padding: '9px 13px', fontSize: '13px' }}
              title="Télécharge le rapport qualité en PDF : vue d’ensemble des 30 derniers jours et le dernier audit de chaque campagne — sans nom de candidat, à transmettre">
              <BI icon={rapportQualite.rapportEnCours ? 'arrow-repeat' : 'file-earmark-pdf'} style={rapportQualite.rapportEnCours ? { animation: 'ats-spin .7s linear infinite' } : { fontSize: '12px' }} />{rapportQualite.rapportEnCours ? 'Génération…' : 'Générer le rapport'}
            </button>
            <button onClick={() => { setCreating(true); setFilter(''); }} style={{ ...PRIMARY_BTN, padding: '9px 14px' }}>
              <BI icon="plus-lg" style={{ fontSize: '12px' }} />Nouvelle campagne
            </button>
          </div>
          )}

          {live ? (
            <SourceurDashboard onOpenBesoin={onOpenBesoin} onSelectBesoin={(id) => onSelectBesoin?.(id)} />
          ) : skinV2 ? (
            /* L'ACCUEIL du Sourceur v2 : « Vos campagnes » — compteurs, liste
               filtrée par état réel et rail « À traiter / Activité récente »,
               tous dérivés de GET /sourcing. */
            <SourceurV2Accueil rows={rows} chargement={campaigns === undefined} routeAbsente={campaigns === null}
              rafraichit={loadingCamp} onSelectBesoin={(id) => onSelectBesoin?.(id)}
              onNouvelleCampagne={() => { setCreating(true); setFilter(''); }}
              onSuiviDirect={() => setLive(true)} onRefresh={loadCampaigns} />
          ) : (
          <>

          {rows.length > 0 && (
            <div style={{ ...CARD, marginTop: '20px', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '13px 18px', borderBottom: '1px solid var(--hair)', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-strong)' }}>Campagnes</span>
                <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px', fontWeight: 600, color: 'var(--text-2)', background: 'var(--code-bg)', padding: '1px 7px', borderRadius: '6px' }}>{filteredRows.length}</span>
                <span style={{ fontSize: '12px', color: 'var(--text-3)' }}>une ligne par besoin chassé — cliquer pour piloter la campagne</span>
                <div style={{ flex: 1 }} />
                {/* Filtre de période — dernière activité */}
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '3px', borderRadius: '99px', background: 'var(--code-bg)', border: '1px solid var(--border)' }}>
                  {[['all', 'Tout'], ['30', '1 mois'], ['90', '3 mois'], ['180', '6 mois']].map(([p, label]) => (
                    <button key={p} type="button" onClick={() => pickPeriod(p)}
                      style={{ padding: '5px 11px', borderRadius: '99px', border: 'none', fontFamily: 'inherit', fontSize: '11.5px', cursor: 'pointer', whiteSpace: 'nowrap',
                        background: period === p ? 'var(--accent)' : 'transparent',
                        color: period === p ? 'var(--accent-fg)' : 'var(--text-2)',
                        fontWeight: period === p ? 600 : 500 }}>{label}</button>
                  ))}
                </div>
              </div>
              {filteredRows.length === 0 && (
                <div style={{ padding: '20px 18px', fontSize: '12.5px', color: 'var(--text-faint)' }}>Aucune campagne sur cette période — élargissez le filtre.</div>
              )}
              {pageRows.map((r, i) => (
                <div key={r.besoin_id ?? i} onClick={r.deleted ? undefined : () => onSelectBesoin?.(r.besoin_id)}
                  className={r.deleted ? undefined : 'src-row-hover'} title={r.error || undefined}
                  style={{ display: 'flex', alignItems: 'center', gap: '13px', padding: '13px 18px', borderBottom: '1px solid var(--hair)', cursor: r.deleted ? 'default' : 'pointer', opacity: r.deleted ? 0.55 : 1 }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '9px', background: 'var(--code-bg)', color: r.deleted ? 'var(--text-faint)' : 'var(--text-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}><BI icon="linkedin" style={{ fontSize: '15px' }} /></div>
                  <div style={{ flex: 1.3, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '-0.01em', color: r.deleted ? 'var(--text-faint)' : 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.deleted ? 'Besoin supprimé' : r.titre}</div>
                    {(r.query || r.experience || r.exclusions.length > 0) && <div style={{ marginTop: '2px', fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px', color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{[r.query, r.experience ? `exp. ${r.experience}` : '', r.exclusions.length ? `sans ${r.exclusions.join(', ')}` : ''].filter(Boolean).join(' · ')}</div>}
                  </div>
                  {r.selLabel && <span style={{ fontSize: '12px', color: 'var(--text-2)', flex: 'none' }}>{r.selLabel}</span>}
                  {r.counter && <span title="InMails envoyés / total" style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '12px', fontWeight: 600, color: 'var(--text-2)', background: 'var(--code-bg)', padding: '3px 9px', borderRadius: '6px', flex: 'none' }}>{r.counter}</span>}
                  <Chip kind={r.chip.k} pulse={r.pulse}>{r.chip.l}</Chip>
                  <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px', color: 'var(--text-faint)', flex: 'none', width: '74px', textAlign: 'right' }}>{fmtDate(r.when)}</span>
                  {!r.deleted && <BI icon="chevron-right" style={{ color: 'var(--text-faint)', fontSize: '12px', flex: 'none' }} />}
                </div>
              ))}
            </div>
          )}

          {/* Pagination pilule */}
          {rows.length > 0 && pages > 1 && (() => {
            const from = Math.max(1, page - 2), to = Math.min(pages, page + 2);
            const win = []; for (let p = from; p <= to; p++) win.push(p);
            return (
              <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
                <div className="ats-pager">
                  <button className="ats-pager-nav" onClick={() => setPage(page - 1)} disabled={page <= 1} aria-label="Page précédente"><BI icon="chevron-left" /></button>
                  {from > 1 && <><button onClick={() => setPage(1)}>1</button>{from > 2 && <span className="ats-pager-gap">…</span>}</>}
                  {win.map(p => <button key={p} onClick={() => setPage(p)} aria-current={p === page ? 'page' : undefined}>{p}</button>)}
                  {to < pages && <>{to < pages - 1 && <span className="ats-pager-gap">…</span>}<button onClick={() => setPage(pages)}>{pages}</button></>}
                  <button className="ats-pager-nav" onClick={() => setPage(page + 1)} disabled={page >= pages} aria-label="Page suivante"><BI icon="chevron-right" /></button>
                </div>
              </div>
            );
          })()}

          {Array.isArray(campaigns) && rows.length === 0 && (
            <div style={{ ...CARD, marginTop: '20px', padding: '56px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', textAlign: 'center' }}>
              <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: 'var(--code-bg)', color: 'var(--text-3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><BI icon="linkedin" style={{ fontSize: '22px' }} /></div>
              <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '18px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>Aucune campagne de sourcing</div>
              <div style={{ maxWidth: '420px', fontSize: '13px', lineHeight: 1.55, color: 'var(--text-2)' }}>Choisissez un besoin, décrivez la cible (ou laissez l'IA générer la recherche booléenne) et Erpia chasse les meilleurs profils LinkedIn.</div>
              <button onClick={() => { setCreating(true); setFilter(''); }} style={{ ...PRIMARY_BTN, marginTop: '4px', padding: '10px 16px' }}>
                <BI icon="plus-lg" style={{ fontSize: '12px' }} />Lancer la première campagne
              </button>
            </div>
          )}
          {campaigns === null && (
            <div style={{ ...CARD, marginTop: '20px', padding: '14px 16px', fontSize: '12.5px', color: 'var(--text-2)' }}>
              Liste des campagnes indisponible pour l'instant — lancez une campagne via « Nouvelle campagne ».
            </div>
          )}
          </>
          )}
        </>
      ) : (
        <>
          {/* ── Vue campagne ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '11px', flexWrap: 'wrap' }}>
            <button onClick={() => { if (creating) { setCreating(false); setPickerOpen(false); } else onClearBesoin?.(); }} title="Retour aux campagnes" className="src-row-hover"
              style={{ ...GHOST_BTN, width: '36px', height: '36px', padding: 0, justifyContent: 'center' }}><BI icon="arrow-left" style={{ fontSize: '14px' }} /></button>
            <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-3)' }}>Sourceur <span style={{ color: 'var(--text-faint)' }}>/</span> <span style={{ fontWeight: 700, color: 'var(--text-strong)' }}>{selected && !creating ? selected.titre : 'Nouvelle campagne'}</span></span>
            <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '10.5px', color: 'var(--text-faint)' }}>/sourceur/{selected && !creating ? selected.id : 'nouveau'}</span>
            <div style={{ flex: 1 }} />
          </div>

          {(creating || pickerOpen || !selected) ? (
            /* Picker besoin */
            <div style={{ ...CARD, marginTop: '16px', overflow: 'hidden' }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--hair)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <div style={{ fontSize: '10.5px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)', flex: 1 }}>Point de départ de la campagne</div>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '3px', borderRadius: '99px', background: 'var(--code-bg)', border: '1px solid var(--border)' }}>
                    <button type="button" onClick={() => setEntree('besoin')} style={{ padding: '5px 12px', borderRadius: '99px', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: '12px', fontWeight: entree === 'besoin' ? 600 : 500, background: entree === 'besoin' ? 'var(--accent)' : 'transparent', color: entree === 'besoin' ? 'var(--accent-fg)' : 'var(--text-2)' }}>Un besoin</button>
                    <button type="button" onClick={() => setEntree('candidat')} style={{ padding: '5px 12px', borderRadius: '99px', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: '12px', fontWeight: entree === 'candidat' ? 600 : 500, background: entree === 'candidat' ? 'var(--accent)' : 'transparent', color: entree === 'candidat' ? 'var(--accent-fg)' : 'var(--text-2)' }}>Un candidat</button>
                  </div>
                </div>
                <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '9px', padding: '0 13px', borderRadius: '9px', border: '1px solid var(--input-border)', background: 'var(--input-bg)' }}>
                  <BI icon="search" style={{ color: 'var(--text-faint)', fontSize: '13px' }} />
                  {(entree === 'besoin' || profilRef)
                    ? <input autoFocus value={filter} onChange={e => setFilter(e.target.value)} placeholder="Rechercher un besoin — titre ou client…"
                        style={{ flex: 1, minWidth: 0, padding: '11px 0', border: 'none', background: 'transparent', color: 'var(--text-strong)', fontFamily: 'inherit', fontSize: '13px', outline: 'none' }} />
                    : <input autoFocus value={candQ} onChange={e => setCandQ(e.target.value)} placeholder="Rechercher un candidat — nom, poste…"
                        style={{ flex: 1, minWidth: 0, padding: '11px 0', border: 'none', background: 'transparent', color: 'var(--text-strong)', fontFamily: 'inherit', fontSize: '13px', outline: 'none' }} />}
                </div>
              </div>
              {entree === 'candidat' && (
                <>
                  {profilRef && (
                    <div style={{ padding: '11px 18px', borderBottom: '1px solid var(--hair)', background: 'var(--code-bg)', fontSize: '12.5px', color: 'var(--text-2)' }}>
                      Profil de référence : <strong style={{ color: 'var(--text-strong)' }}>{profilRef.nom}</strong> — choisissez maintenant le besoin auquel rattacher cette campagne.
                    </div>
                  )}
                  {!profilRef && cands === null && <div style={{ padding: '14px 18px', fontSize: '12.5px', color: 'var(--text-faint)' }}>Chargement des candidats…</div>}
                  {!profilRef && Array.isArray(cands) && cands.length === 0 && <div style={{ padding: '14px 18px', fontSize: '12.5px', color: 'var(--text-faint)' }}>Aucun candidat ne correspond.</div>}
                  {!profilRef && (cands || []).map(c => {
                    const nom = [c.prenom, c.nom].filter(Boolean).join(' ').trim() || `Candidat ${c.id}`;
                    const texte = profilDe(c);
                    return (
                      <div key={c.id} onClick={() => setProfilRef({ id: c.id, nom, texte })} className="src-row-hover"
                        style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 18px', borderBottom: '1px solid var(--hair)', cursor: 'pointer' }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'var(--code-bg)', color: 'var(--text-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}><BI icon="person" style={{ fontSize: '13px' }} /></div>
                        <span style={{ flex: 1, minWidth: 0, fontSize: '13.5px', fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {nom} {(c.titre || c.poste) && <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>· {c.titre || c.poste}</span>}
                        </span>
                        {!texte && <Chip kind="warn">fiche peu remplie</Chip>}
                      </div>
                    );
                  })}
                  {!profilRef && (
                    <div style={{ padding: '11px 18px', fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.55 }}>
                      La campagne cherchera des profils comparables à ce candidat. Elle reste rattachée
                      à un besoin, que vous choisirez juste après.
                    </div>
                  )}
                </>
              )}
              {(entree === 'besoin' || profilRef) && loadingBesoins && <div style={{ padding: '14px 18px', fontSize: '12.5px', color: 'var(--text-faint)' }}>Chargement des besoins…</div>}
              {(entree === 'besoin' || profilRef) && !loadingBesoins && matches.length === 0 && <div style={{ padding: '14px 18px', fontSize: '12.5px', color: 'var(--text-faint)' }}>Aucun besoin ne correspond.</div>}
              {(entree === 'besoin' || profilRef) && matches.slice(0, 8).map(b => (
                <div key={b.id} onClick={() => pick(b)} className="src-row-hover"
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 18px', borderBottom: '1px solid var(--hair)', cursor: 'pointer' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'var(--code-bg)', color: 'var(--text-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}><BI icon="grid" style={{ fontSize: '13px' }} /></div>
                  <span style={{ flex: 1, minWidth: 0, fontSize: '13.5px', fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.titre} {b.client && <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>· {b.client}</span>}</span>
                  {b.statut && <Chip kind="mut">{b.statut}</Chip>}
                </div>
              ))}
              {selected && !creating && (
                <div style={{ padding: '10px 18px' }}>
                  <button onClick={() => setPickerOpen(false)} className="src-row-hover" style={{ ...GHOST_BTN, padding: '7px 12px', fontSize: '12px' }}>Annuler</button>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Carte besoin */}
              <div style={{ ...CARD, marginTop: '16px', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '13px', flexWrap: 'wrap' }}>
                <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: 'var(--accent-soft-bg)', color: 'var(--accent-soft-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}><BI icon="grid" style={{ fontSize: '15px' }} /></div>
                <div style={{ flex: 1, minWidth: '220px' }}>
                  <div style={{ fontSize: '10.5px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Besoin de la campagne</div>
                  <div style={{ marginTop: '2px', fontSize: '15px', fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--text-strong)' }}>{selected.titre} {selected.client && <span style={{ fontWeight: 400, color: 'var(--text-3)' }}>· {selected.client}</span>}</div>
                </div>
                {onOpenBesoin && <button onClick={() => onOpenBesoin(selected.id)} className="src-row-hover" style={GHOST_BTN}><BI icon="box-arrow-up-right" style={{ fontSize: '12px' }} />Ouvrir le besoin</button>}
                <button onClick={() => { setPickerOpen(true); setFilter(''); }} className="src-row-hover"
                  style={{ padding: '8px 12px', borderRadius: '8px', border: 'none', background: 'transparent', color: 'var(--text-3)', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', flex: 'none' }}>Changer</button>
              </div>
              <BesoinCampaign key={selected.id} profilRef={profilRef} besoin={selected} showNotification={showNotification} onActivity={loadCampaigns} onOpenCandidat={onOpenCandidat}
                rows={rows} onSelectBesoin={(id) => onSelectBesoin?.(id)} onNouvelle={() => { setCreating(true); setFilter(''); }} />
            </>
          )}
        </>
      )}
    </div>
  );
}

// Campagne d'un besoin : formulaire (brouillon / nouvelle recherche) + états.
function BesoinCampaign({ profilRef, besoin, showNotification, onActivity, onOpenCandidat, rows, onSelectBesoin, onNouvelle }) {
  const sourcing = useSourcing(besoin, { showNotification, enabled: true });
  const { search, loading, reload } = sourcing;
  const [formOpen, setFormOpen] = useState(false);
  const showForm = formOpen || (!search && !loading);

  return (
    <div className="spl-grille">
      <RailCampagnes rows={rows} courant={besoin?.id} onSelect={onSelectBesoin} onNouvelle={onNouvelle} />
      <div style={{ minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ flex: 1 }} />
        <button onClick={() => { reload(); onActivity?.(); }} disabled={loading} className="src-row-hover" style={GHOST_BTN}>
          <BI icon="arrow-repeat" style={loading ? { animation: 'ats-spin .7s linear infinite' } : undefined} />Rafraîchir
        </button>
        {search && !showForm && (
          <button onClick={() => setFormOpen(true)} className="src-row-hover" style={GHOST_BTN}>
            <BI icon="plus-lg" style={{ fontSize: '11px' }} />Nouvelle recherche
          </button>
        )}
      </div>
      {!showForm && <BandeauNotifiesBesoin besoinId={besoin?.id} showNotification={showNotification} />}
      {showForm && <LaunchForm profilRef={profilRef} besoin={besoin} sourcing={sourcing} onLaunched={() => { setFormOpen(false); onActivity?.(); }} onCancel={search ? () => setFormOpen(false) : null} />}
      <CampaignPanel besoin={besoin} sourcing={sourcing} showNotification={showNotification} onEditSearch={() => setFormOpen(true)} onOpenCandidat={onOpenCandidat} />
      </div>
    </div>
  );
}
